import React, { useState, useEffect, useCallback, useRef } from 'react';
import { saveFile, buildPdf, isNativeApp, isTouchDevice } from '../../../_utils/fileExport';
import '../../shared/sheet-animations.css';
import { Card } from '../components/Card';
import DateRangePicker from '../components/DateRangePicker';
import { ChevronDownIcon, DocumentIcon, ChevronRightIcon } from '../icons/FinanceIcons';
import { reportCategories, getDatePresetLabel } from '../data/mockData';
import { financeService } from '../../../_services/finance.service';
import useWindowSize from '../../new-service/useWindowSize';
import useSites from '../../multiLocation/useSites';
import { reportRefunds, reportNetTakings, rowRefunds, netAfterRefunds, refundCountLabel, minusMoney, money as refundMoney } from '../../refunds/refundLogic';

// Helper to format date as YYYY-MM-DD
// "4 Sept - 4 Sept 2026" read as one day when it was 4 Sept 2025 → 4 Sept 2026
// (Cropshop, last 12 months). Print the start year whenever it differs.
const fmtRangeLabel = (start, end) => {
  const sameYear = start.getFullYear() === end.getFullYear();
  const s = start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
  const e = end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${s} - ${e}`;
};

const formatDateForApi = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to get date range from preset
const getDateRangeFromPreset = (preset) => {
  const now = new Date();
  let startDate, endDate;

  switch (preset) {
    case 'today':
      startDate = endDate = new Date(now);
      break;
    case 'yesterday':
      startDate = endDate = new Date(new Date().setDate(new Date().getDate() - 1));
      break;
    case 'last7':
      endDate = new Date();
      startDate = new Date(new Date().setDate(new Date().getDate() - 7));
      break;
    case 'last4weeks':
      endDate = new Date();
      startDate = new Date(new Date().setDate(new Date().getDate() - 28));
      break;
    case 'last6months':
      endDate = new Date();
      startDate = new Date(new Date().setMonth(new Date().getMonth() - 6));
      break;
    case 'last12months':
      endDate = new Date();
      startDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
      break;
    case 'wtd':
      endDate = new Date();
      startDate = new Date(new Date().setDate(new Date().getDate() - new Date().getDay()));
      break;
    case 'mtd':
      endDate = new Date();
      startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      break;
    case 'qtd':
      endDate = new Date();
      const quarter = Math.floor(new Date().getMonth() / 3);
      startDate = new Date(new Date().getFullYear(), quarter * 3, 1);
      break;
    case 'ytd':
      endDate = new Date();
      startDate = new Date(new Date().getFullYear(), 0, 1);
      break;
    default:
      endDate = new Date();
      startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  }

  return { startDate, endDate };
};

// Staff Earnings: serviceRate is the stylist's staff DEFAULT. When some of
// their services override it (per-service commission), say so rather than
// implying one rate applied to everything.
const serviceRateLabel = (r) => {
  const n = r && r.serviceOverrideCount ? r.serviceOverrideCount : 0;
  return n > 0 ? `${r.serviceRate}% default, ${n} service${n === 1 ? '' : 's'} differ` : `${r.serviceRate}%`;
};

// A by-hand line starts from the price after any code (= menu when there's none).
const manualFrom = (l) => Math.round(((Number(l.charged) || 0) + (Number(l.off) || 0) / Math.max(1, Number(l.qty) || 1)) * 100) / 100;
const fmt = (v, prefix = '£') => v != null && v !== 0 ? `${prefix}${Number(v).toLocaleString(undefined, { minimumFractionDigits: prefix === '£' ? 2 : 0, maximumFractionDigits: 2 })}` : `${prefix}0`;

// What each money report counts as its takings, for "net takings" when the
// server doesn't send one (docs/refunds.md: net = sales minus refunds made).
const reportGross = (reportType, data) => {
  if (!data) return 0;
  switch (reportType) {
    case 'revenue-summary': return (data.revenue || []).reduce((s, r) => s + (r.revenue || 0), 0);
    case 'end-of-day': return (data.summary && data.summary.totalRevenue) || 0;
    case 'payment-methods': return (data.breakdown || []).reduce((s, b) => s + (b.total || 0), 0);
    case 'sales-by-staff': return (data.salesByStaff || []).reduce((s, st) => s + (st.totalRevenue || 0), 0);
    case 'staff-earnings': return (data.totals && data.totals.grossTakings) || 0;
    default: return null;
  }
};

// Refunds made in the period plus net takings, or null (no refunds: nothing shown).
const reportRefundSummary = (reportType, data) => {
  const rf = reportRefunds(reportType, data);
  if (!rf) return null;
  const explicit = reportNetTakings(data);
  const gross = reportGross(reportType, data);
  const net = explicit != null ? explicit : (gross != null ? netAfterRefunds(gross, rf) : null);
  return { ...rf, net };
};

// Metrics plus a Refunds and a Net takings tile when refunds were made.
const getReportMetrics = (reportType, data) => {
  const base = baseReportMetrics(reportType, data);
  // Tips refunded in the period come off tips, not takings.
  if (reportType === 'tips-report' && data) {
    const refundedTips = (data.tips || []).reduce((s, t) => s + (Number(t.refundedTips) || 0), 0);
    if (refundedTips > 0) return [...base, { label: 'Tips refunded', value: minusMoney(refundedTips) }];
  }
  const rs = reportRefundSummary(reportType, data);
  if (!rs) return base;
  return [
    ...base,
    { label: rs.count ? `Refunds (${rs.count})` : 'Refunds', value: minusMoney(rs.amount) },
    ...(rs.net != null ? [{ label: 'Net takings', value: refundMoney(rs.net) }] : []),
  ];
};

// Extract report-type-specific summary metrics from API response data
const baseReportMetrics = (reportType, data) => {
  if (!data) return [{ label: 'No data', value: '-' }];

  switch (reportType) {
    case 'revenue-summary': {
      const rev = data.revenue || [];
      const total = rev.reduce((s, r) => s + (r.revenue || 0), 0);
      const txns = rev.reduce((s, r) => s + (r.transactionCount || 0), 0);
      return [
        { label: 'Total Revenue', value: fmt(total) },
        { label: 'Transactions', value: txns },
        { label: 'Avg. Value', value: fmt(txns > 0 ? total / txns : 0) }
      ];
    }
    case 'end-of-day': {
      const s = data.summary || {};
      return [
        { label: 'Total Revenue', value: fmt(s.totalRevenue) },
        { label: 'Transactions', value: s.totalTransactions || 0 },
        { label: 'Payment Types', value: (data.paymentBreakdown || []).length }
      ];
    }
    case 'payment-methods': {
      const bd = data.breakdown || [];
      const total = bd.reduce((s, b) => s + (b.total || 0), 0);
      return [
        { label: 'Total Revenue', value: fmt(total) },
        { label: 'Payment Types', value: bd.length },
        { label: 'Transactions', value: bd.reduce((s, b) => s + (b.count || 0), 0) }
      ];
    }
    case 'sales-by-staff': {
      const staff = data.salesByStaff || [];
      const total = staff.reduce((s, st) => s + (st.totalRevenue || 0), 0);
      return [
        { label: 'Total Revenue', value: fmt(total) },
        { label: 'Staff Members', value: staff.length },
        { label: 'Avg. per Staff', value: fmt(staff.length > 0 ? total / staff.length : 0) }
      ];
    }
    case 'vat-report': {
      const t = data.totals || {};
      const rf = data.refunded || {};
      // Refunds made in the period (Stripe refunds, dated when made) reduce the tax due.
      if (rf.refunds > 0) {
        return [
          { label: 'Tax collected', value: fmt(t.tax) },
          { label: `Refunded (${rf.refunds})`, value: fmt(-(rf.tax || 0)) },
          { label: 'Tax due', value: fmt(data.due) }
        ];
      }
      return [
        { label: 'Tax collected', value: fmt(t.tax) },
        { label: 'Net sales', value: fmt(t.net) },
        { label: 'Gross sales', value: fmt(t.gross) }
      ];
    }
    case 'staff-earnings': {
      const t = data.totals || {};
      return [
        { label: 'Salon Turnover', value: fmt(t.salonTurnover) },
        { label: 'SE Pass-through', value: fmt(t.sePassThrough) },
        { label: 'Gross Takings', value: fmt(t.grossTakings) }
      ];
    }
    case 'commission-payroll': {
      const comm = data.commission || [];
      const total = comm.reduce((s, c) => s + (c.totalCommission || 0), 0);
      return [
        { label: 'Total Commission', value: fmt(total) },
        { label: 'Staff Members', value: comm.length },
        { label: 'Avg. per Staff', value: fmt(comm.length > 0 ? total / comm.length : 0) }
      ];
    }
    case 'manual-discounts': {
      const t = (data.manual && data.manual.totals) || {};
      const c = (data.summary && data.summary.codes) || {};
      return [
        { label: 'Discount codes', value: fmt(c.total || 0) },
        { label: 'Taken off by hand', value: fmt(t.amount || 0) },
        { label: 'Sales by hand without Discounts', value: t.withoutRight || 0 }
      ];
    }
    case 'tips-report': {
      const tips = data.tips || [];
      const total = tips.reduce((s, t) => s + (t.totalTips || 0), 0);
      return [
        { label: 'Total Tips', value: fmt(total) },
        { label: 'Staff Members', value: tips.length },
        { label: 'Total Bookings', value: tips.reduce((s, t) => s + (t.tipCount || 0), 0) }
      ];
    }
    case 'holiday-report': {
      const h = data.holidays || {};
      // totalsByType is new from the BE. Older deploys won't have it —
      // fall back to a zeroed map so the FE doesn't crash, and still
      // shows the totalDays headline.
      const byType = h.totalsByType || { holiday: 0, sick: 0, other: 0 };
      return [
        { label: 'Total Days Off', value: h.totalDays || 0 },
        { label: 'Holiday', value: byType.holiday || 0 },
        { label: 'Sick', value: byType.sick || 0 },
        { label: 'Other', value: byType.other || 0 },
        { label: 'Staff with Leave', value: (h.staff || []).length }
      ];
    }
    case 'seat-utilisation': {
      const u = data.utilisation || {};
      const o = u.overall || {};
      return [
        { label: 'Utilisation Rate', value: `${o.rate || 0}%` },
        { label: 'Booked Hours', value: Math.round((o.bookedMinutes || 0) / 60) },
        { label: 'Available Hours', value: Math.round((o.availableMinutes || 0) / 60) }
      ];
    }
    case 'rebook-rate': {
      const r = data.rebooking || {};
      return [
        { label: 'Rebook Rate', value: `${r.value || 0}%` },
        { label: 'Rebooked', value: r.rebooked || 0 },
        { label: 'Total Appointments', value: r.total || 0 }
      ];
    }
    case 'client-retention': {
      const o = data.overall || {};
      return [
        { label: 'Retention Rate', value: `${o.rate || 0}%` },
        { label: 'Total Clients', value: o.totalClients || 0 },
        { label: 'Retained', value: o.retainedClients || 0 },
        { label: 'Avg Spend', value: fmt(o.avgSpend) }
      ];
    }
    case 'new-vs-returning': {
      const r = data.retention || {};
      return [
        { label: 'New Clients', value: r.newClients || 0 },
        { label: 'Returning', value: r.returningClients || 0 },
        { label: 'Return Rate', value: `${r.returningClientPercentage || 0}%` }
      ];
    }
    case 'product-sales': {
      const p = data.products || [];
      const total = p.reduce((s, pr) => s + (pr.totalRevenue || 0), 0);
      return [
        { label: 'Product Revenue', value: fmt(total) },
        { label: 'Products Sold', value: p.reduce((s, pr) => s + (pr.totalQuantity || 0), 0) },
        { label: 'Unique Products', value: p.length }
      ];
    }
    case 'inventory-report': {
      const inv = data.inventory || {};
      const s = inv.summary || {};
      return [
        { label: 'Stock Value (Cost)', value: fmt(s.totalStockValue) },
        { label: 'In Stock', value: s.inStock || 0 },
        { label: 'Out of Stock', value: s.outOfStock || 0 }
      ];
    }
    case 'gift-vouchers': {
      const v = data.vouchers || {};
      const outst = Math.round(((v.sold?.total || 0) - (v.redeemed?.total || 0)) * 100) / 100;
      return [
        { label: 'Sold', value: fmt(v.sold?.total) },
        { label: 'Redeemed', value: fmt(v.redeemed?.total) },
        { label: 'Outstanding', value: fmt(outst) },
        { label: 'Vouchers Sold', value: v.sold?.count || 0 }
      ];
    }
    case 'loyalty': {
      const l = data.loyalty || {};
      return [
        { label: 'Rewards Redeemed', value: fmt(l.redeemed?.total) },
        { label: 'Count', value: l.redeemed?.count || 0 },
        { label: 'Banked Rewards Owed', value: l.liability?.bankedRewards || 0 }
      ];
    }
    default:
      return [{ label: 'Report Generated', value: '✓' }];
  }
};

// ── Premium Report Detail ────────────────────────────────────────
const detailStyles = {
  section: { marginBottom: '20px' },
  sectionLabel: { fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 10px 0' },
  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0 },
  th: { textAlign: 'left', padding: '8px 14px', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' },
  thR: { textAlign: 'right', padding: '8px 14px', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' },
  td: { padding: '10px 14px', fontSize: '13px', color: '#1f2937', borderBottom: '1px solid #f3f4f6' },
  tdR: { padding: '10px 14px', fontSize: '13px', color: '#1f2937', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: '500' },
  tdBold: { padding: '10px 14px', fontSize: '13px', color: '#1f2937', borderBottom: '1px solid #f3f4f6', fontWeight: '600' },
  foot: { padding: '10px 14px', fontSize: '13px', fontWeight: '600', color: '#1f2937', borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb' },
  footR: { padding: '10px 14px', fontSize: '13px', fontWeight: '600', color: '#1f2937', borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  empty: { fontSize: '13px', color: '#9ca3af', textAlign: 'center', padding: '32px 16px' },
  bar: (pct, color = '#007EA0') => ({ width: `${Math.min(pct, 100)}%`, height: '6px', borderRadius: '3px', backgroundColor: color, transition: 'width 0.4s ease' }),
  barBg: { width: '100%', height: '6px', borderRadius: '3px', backgroundColor: '#f3f4f6' },
  pill: (color) => {
    const bg = color === 'red' ? '#fef2f2'
      : color === 'green' ? '#f0fdf4'
      : color === 'amber' ? '#fffbeb'
      : color === 'gray' ? '#f3f4f6'
      : '#f0f9ff';
    const fg = color === 'red' ? '#dc2626'
      : color === 'green' ? '#16a34a'
      : color === 'amber' ? '#b45309'
      : color === 'gray' ? '#4b5563'
      : '#007EA0';
    return { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: bg, color: fg };
  },
};

const ScrollTable = ({ children }) => (
  <div style={{ borderRadius: '10px', border: '1px solid #e5e7eb', marginBottom: '20px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
    <table style={detailStyles.table}>{children}</table>
  </div>
);

const EmptyState = ({ msg }) => <p style={detailStyles.empty}>{msg}</p>;

// Refunds made in the period, under any report that has them.
const RefundTotals = ({ reportType, data }) => {
  const rs = reportRefundSummary(reportType, data);
  if (!rs) return null;
  const row = (label, value, strong, colour) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ fontSize: '13px', fontWeight: strong ? '700' : '600', color: '#1a1a1a' }}>{label}</span>
      <span style={{ fontSize: strong ? '15px' : '13px', fontWeight: strong ? '700' : '600', color: colour || '#1a1a1a', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
  return (
    <div style={detailStyles.section}>
      <p style={detailStyles.sectionLabel}>Refunds</p>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '2px 14px' }}>
        {row(`Refunds${rs.count ? ` · ${refundCountLabel(rs.count)}` : ''}`, minusMoney(rs.amount), false, '#b42318')}
        {rs.net != null ? row('Net takings', refundMoney(rs.net), true) : null}
      </div>
      <p style={{ fontSize: '11px', color: '#9ca3af', margin: '10px 0 0 0', lineHeight: 1.4 }}>Refunds count on the day they were made. The sales they came from stay in their own period.</p>
    </div>
  );
};

const ReportDetail = (props) => (
  <>
    <ReportDetailBody {...props} />
    {props.data ? <RefundTotals reportType={props.reportType} data={props.data} /> : null}
  </>
);

const ReportDetailBody = ({ reportType, data, rebookThreshold, retentionThreshold, staffSortKey, staffSortDir, onStaffSort, isMobile = false }) => {
  if (!data) return null;

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  switch (reportType) {
    case 'holiday-report': {
      const staff = data.holidays?.staff || [];
      if (!staff.length) return <EmptyState msg="No holidays recorded in this period" />;
      const totalDays = staff.reduce((s, st) => s + st.holidays.reduce((a, h) => a + (h.days || 0), 0), 0);
      const leaveTypeLabel = (t) => {
        if (t === 'sick') return 'Sick';
        if (t === 'other') return 'Other';
        return 'Holiday';
      };
      const leaveTypeColor = (t) => {
        if (t === 'sick') return 'amber';
        if (t === 'other') return 'gray';
        return 'blue';
      };
      return (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionLabel}>Holiday Breakdown</p>
          <ScrollTable>
            <thead><tr>
              <th style={detailStyles.th}>Staff Member</th>
              <th style={detailStyles.th}>Period</th>
              <th style={detailStyles.th}>Type</th>
              <th style={detailStyles.th}>Reason</th>
              <th style={detailStyles.thR}>Days</th>
            </tr></thead>
            <tbody>
              {staff.flatMap(s => s.holidays.map((h, i) => (
                <tr key={`${s.staffId}-${i}`} style={i === 0 ? { borderTop: staff.indexOf(s) > 0 ? '2px solid #f3f4f6' : 'none' } : {}}>
                  <td style={i === 0 ? detailStyles.tdBold : { ...detailStyles.td, color: '#9ca3af' }}>{i === 0 ? s.staffName : ''}</td>
                  <td style={detailStyles.td}>{fmtDate(h.startDate)} – {fmtDate(h.endDate)}</td>
                  <td style={detailStyles.td}><span style={detailStyles.pill(leaveTypeColor(h.leaveType))}>{leaveTypeLabel(h.leaveType)}</span></td>
                  <td style={detailStyles.td}>{h.title || ''}</td>
                  <td style={detailStyles.tdR}>{h.days}</td>
                </tr>
              )))}
            </tbody>
            <tfoot><tr>
              <td style={detailStyles.foot} colSpan={4}>Total</td>
              <td style={detailStyles.footR}>{totalDays}</td>
            </tr></tfoot>
          </ScrollTable>
        </div>
      );
    }
    case 'seat-utilisation': {
      const staff = data.utilisation?.staff || [];
      if (!staff.length) return <EmptyState msg="No utilisation data available" />;
      return (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionLabel}>Staff Utilisation</p>
          <ScrollTable>
            <thead><tr>
              <th style={detailStyles.th}>Staff Member</th>
              <th style={{ ...detailStyles.th, width: '120px' }}>Utilisation</th>
              <th style={detailStyles.thR}>Booked</th>
              <th style={detailStyles.thR}>Available</th>
              <th style={detailStyles.thR}>Appts</th>
            </tr></thead>
            <tbody>
              {staff.map(s => (
                <tr key={s.staffId}>
                  <td style={detailStyles.tdBold}>{s.staffName}</td>
                  <td style={detailStyles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={detailStyles.barBg}><div style={detailStyles.bar(s.rate, s.rate >= 80 ? '#16a34a' : s.rate >= 50 ? '#007EA0' : '#f59e0b')} /></div>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', minWidth: '32px' }}>{s.rate}%</span>
                    </div>
                  </td>
                  <td style={detailStyles.tdR}>{Math.round(s.bookedMinutes / 60)}h</td>
                  <td style={detailStyles.tdR}>{Math.round(s.availableMinutes / 60)}h</td>
                  <td style={detailStyles.tdR}>{s.bookingCount}</td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        </div>
      );
    }
    case 'sales-by-staff': {
      const staff = data.salesByStaff || [];
      if (!staff.length) return <EmptyState msg="No sales data available" />;
      const staffRefunds = staff.some((st) => rowRefunds(st));
      const sk = staffSortKey || 'totalRevenue';
      const sd = staffSortDir || 'desc';
      const sorted = [...staff].sort((a, b) => sd === 'desc' ? (b[sk] || 0) - (a[sk] || 0) : (a[sk] || 0) - (b[sk] || 0));
      const sortTh = (label, field) => (
        <th style={{ ...detailStyles.thR, cursor: 'pointer', userSelect: 'none' }} onClick={() => onStaffSort && onStaffSort(field)}>
          {label}{sk === field ? (sd === 'desc' ? ' ↓' : ' ↑') : ''}
        </th>
      );
      return (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionLabel}>Revenue by Staff</p>
          <ScrollTable>
            <thead><tr>
              <th style={detailStyles.th}>Staff</th>
              {sortTh('Services', 'serviceRevenue')}
              {sortTh('Products', 'productRevenue')}
              {sortTh('Vouchers', 'voucherRevenue')}
              {sortTh('Tips', 'tips')}
              {staffRefunds && <th style={detailStyles.thR}>Refunds</th>}
              {sortTh('Total', 'totalRevenue')}
              {sortTh('Appts', 'bookingCount')}
            </tr></thead>
            <tbody>
              {sorted.map(s => (
                <tr key={s.staffId || s.name}>
                  <td style={detailStyles.tdBold}>{s.name}</td>
                  <td style={detailStyles.tdR}>{fmt(s.serviceRevenue)}</td>
                  <td style={detailStyles.tdR}>{fmt(s.productRevenue)}</td>
                  <td style={detailStyles.tdR}>{fmt(s.voucherRevenue)}</td>
                  <td style={detailStyles.tdR}>{fmt(s.tips)}</td>
                  {staffRefunds && <td style={{ ...detailStyles.tdR, color: rowRefunds(s) ? '#b42318' : undefined }}>{rowRefunds(s) ? minusMoney(rowRefunds(s).amount) : '£0'}</td>}
                  <td style={{ ...detailStyles.tdR, fontWeight: '600' }}>{fmt(s.totalRevenue)}</td>
                  <td style={detailStyles.tdR}>{s.bookingCount}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td style={detailStyles.foot}>Total</td>
              <td style={detailStyles.footR}>{fmt(staff.reduce((s, st) => s + (st.serviceRevenue || 0), 0))}</td>
              <td style={detailStyles.footR}>{fmt(staff.reduce((s, st) => s + (st.productRevenue || 0), 0))}</td>
              <td style={detailStyles.footR}>{fmt(staff.reduce((s, st) => s + (st.voucherRevenue || 0), 0))}</td>
              <td style={detailStyles.footR}>{fmt(staff.reduce((s, st) => s + (st.tips || 0), 0))}</td>
              {staffRefunds && <td style={detailStyles.footR}>{minusMoney(staff.reduce((s, st) => s + ((rowRefunds(st) || {}).amount || 0), 0))}</td>}
              <td style={detailStyles.footR}>{fmt(staff.reduce((s, st) => s + (st.totalRevenue || 0), 0))}</td>
              <td style={detailStyles.footR}>{staff.reduce((s, st) => s + (st.bookingCount || 0), 0)}</td>
            </tr></tfoot>
          </ScrollTable>
        </div>
      );
    }
    case 'staff-earnings': {
      const business = data.business || [];
      const se = data.selfEmployed || [];
      const t = data.totals || {};
      const bt = t.business || {};
      const st = t.selfEmployed || {};
      if (!business.length && !se.length) return <EmptyState msg="No earnings data available" />;
      const typeLabel = (r) => r.type === 'owner' ? 'Owner' : r.type === 'employed' ? 'Employed' : r.type === 'online-store' ? 'Online store' : r.type === 'unassigned' ? 'Unassigned' : 'Self-employed';
      const feeNote = data.feePaidBy === 'salon' ? 'Borne by the salon.' : data.feePaidBy === 'stylist' ? 'Borne by the self-employed on their own bookings, by the salon on employed bookings.' : '';
      const num = (v) => fmt(v);
      const leftTag = (r) => r.active === false ? <span style={{ fontWeight: 400, color: '#9ca3af' }}> (left)</span> : null;
      // Refunds made in the period, per person (docs/refunds.md). Columns only when there are any.
      const hasRf = [...business, ...se].some((r) => Number(r.refunds) > 0);
      const rfCell = (v) => <td style={{ ...detailStyles.tdR, color: Number(v) > 0 ? '#b42318' : undefined }}>{Number(v) > 0 ? minusMoney(v) : num(0)}</td>;
      const rfFoot = (v) => <td style={detailStyles.footR}>{Number(v) > 0 ? minusMoney(v) : num(0)}</td>;
      // Section tax total: the server's figure when it sends one, else the rows summed.
      const taxTotal = (tot, rows) => (tot.tax != null ? Number(tot.tax) || 0
        : Math.round(rows.reduce((s, r) => s + (Number(r.tax) || 0), 0) * 100) / 100);

      // ---- Totals: a label / value list reads well at every width ----
      const totalRow = (label, value, note, strong) => (
        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: '13px', fontWeight: strong ? '700' : '600', color: '#1a1a1a', margin: 0 }}>{label}</p>
            {note ? <p style={{ fontSize: '11px', color: '#9ca3af', margin: '2px 0 0 0', lineHeight: 1.4 }}>{note}</p> : null}
          </div>
          <p style={{ fontSize: strong ? '15px' : '13px', fontWeight: strong ? '700' : '600', color: '#1a1a1a', margin: 0, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{typeof value === 'string' ? value : num(value)}</p>
        </div>
      );
      const totalsBlock = (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionLabel}>Totals for the period</p>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '2px 14px' }}>
            {totalRow('Salon turnover', t.salonTurnover, 'Business "to business" plus self-employed "to business". Net of card fees where the salon bore them.', true)}
            {totalRow('Self-employed pass-through', t.sePassThrough, 'Money belonging to the self-employed. Not salon turnover.', true)}
            {totalRow('Gross takings', t.grossTakings, 'Services, products, vouchers and no-show / cancellation charges before fees. Excludes tips.')}
            {Number(t.refunds) > 0 ? totalRow('Refunds', minusMoney(t.refunds), `${t.refundCount ? `${refundCountLabel(t.refundCount)} made in this period, ` : 'Made in this period, '}taken back from whoever received the money. Tips refunded come off tips.`) : null}
            {Number(t.refunds) > 0 && t.netSalonTurnover != null ? totalRow('Salon turnover after refunds', t.netSalonTurnover, null, true) : null}
            {Number(t.refunds) > 0 && t.netSePassThrough != null ? totalRow('Self-employed pass-through after refunds', t.netSePassThrough) : null}
            {totalRow('Card fees', t.fees, feeNote)}
            {totalRow('Tips', t.tips, 'Paid to the stylist. Outside the split, not turnover.')}
            {t.loyaltyComps > 0 ? totalRow('Loyalty comps', t.loyaltyComps, 'Notional value of rewards given away; sits inside services, no cash collected.') : null}
            {totalRow('Owed in cash by self-employed', st.owedInCash, 'Salon’s share of money the self-employed took outside Beu. Matches the Commission Tracker for the same dates.')}
          </div>
          <p style={{ fontSize: '11px', color: '#9ca3af', margin: '10px 0 0 0', lineHeight: 1.4 }}>Period by appointment date, matching Overview. “Via Beu” was split by Stripe at payment; “owed in cash” is what the stylist still settles with the salon. Card fees on no-show and cancellation charges are not attributed.</p>
        </div>
      );

      // ---- Mobile: one card per person, zero lines hidden ----
      if (isMobile) {
        const lines = (r, isSe) => [
          ['Services', r.services], ['Products', r.products], ['Vouchers', r.vouchers], ['No-show / cancel', r.noShowCancelFees],
          ['Gross', r.gross, true], ['Refunds', Number(r.refunds) || 0], ['Tax in gross', r.tax || 0], ['Card fees', r.fees],
          ...(isSe ? [['To stylist', r.toStylist, true], ['Via Beu', r.settledViaBeu], ['Owed in cash', r.owedInCash], ['Paid to salon', r.paidToSalon]] : []),
          ['Tips', r.tips], ['Appointments', r.bookings]
        ].filter(([, v, always]) => always || Number(v) !== 0);
        const card = (r, isSe) => (
          <div key={r.staffId} style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '12px 14px', marginBottom: '10px', backgroundColor: 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}{leftTag(r)}</p>
                <p style={{ fontSize: '11px', color: '#9ca3af', margin: '2px 0 0 0' }}>{isSe ? `Self-employed · ${serviceRateLabel(r)}${r.productRate ? ` / ${r.productRate}%` : ''}` : typeLabel(r)}</p>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ fontSize: '10px', fontWeight: '600', color: '#9ca3af', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>To business</p>
                <p style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a', margin: 0, fontVariantNumeric: 'tabular-nums' }}>{num(r.toBusiness)}</p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '16px', rowGap: '5px', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f3f4f6' }}>
              {lines(r, isSe).map(([l, v, strong]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '12px', minWidth: 0 }}>
                  <span style={{ color: '#737373', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l}</span>
                  <span style={{ color: '#1a1a1a', fontWeight: strong ? '700' : '500', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{l === 'Appointments' ? v : l === 'Refunds' ? minusMoney(v) : num(v)}</span>
                </div>
              ))}
            </div>
          </div>
        );
        const sectionTotal = (label, tot, isSe) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '10px 14px', borderRadius: '10px', backgroundColor: '#f9fafb', marginBottom: '4px', fontSize: '12px' }}>
            <span style={{ fontWeight: '600', color: '#1a1a1a' }}>{label}</span>
            <span style={{ color: '#1a1a1a', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
              <span style={{ color: '#737373' }}>Gross </span><strong>{num(tot.gross)}</strong>
              <span style={{ color: '#737373' }}> · To business </span><strong>{num(tot.toBusiness)}</strong>
              {isSe ? <><span style={{ color: '#737373' }}> · To stylist </span><strong>{num(tot.toStylist)}</strong></> : null}
            </span>
          </div>
        );
        return (
          <div>
            <div style={detailStyles.section}>
              <p style={detailStyles.sectionLabel}>Business</p>
              <p style={{ fontSize: '11px', color: '#9ca3af', margin: '-6px 0 10px 0' }}>Owner, employed staff, online store. Every pound is salon turnover.</p>
              {business.map(r => card(r, false))}
              {sectionTotal('Business total', bt, false)}
            </div>
            <div style={detailStyles.section}>
              <p style={detailStyles.sectionLabel}>Self-employed</p>
              <p style={{ fontSize: '11px', color: '#9ca3af', margin: '-6px 0 10px 0' }}>Gross is pass-through; only “to business” is salon turnover.</p>
              {se.length ? se.map(r => card(r, true)) : <EmptyState msg="No self-employed staff in this period" />}
              {se.length ? sectionTotal('Self-employed total', st, true) : null}
            </div>
            {totalsBlock}
          </div>
        );
      }

      // ---- Desktop: two ledgers (the modal is widened for this report; ScrollTable scrolls sideways beyond that) ----
      const nameCell = (r) => <td style={{ ...detailStyles.tdBold, whiteSpace: 'nowrap' }}>{r.name}{leftTag(r)}</td>;
      const th = (label) => <th key={label} style={{ ...detailStyles.thR, whiteSpace: 'nowrap' }}>{label}</th>;
      return (
        <div>
          <div style={detailStyles.section}>
            <p style={detailStyles.sectionLabel}>Business — owner, employed staff, online store (every pound is salon turnover)</p>
            <ScrollTable>
              <thead><tr>
                <th style={detailStyles.th}>Staff</th>
                <th style={detailStyles.th}>Type</th>
                {['Services', 'Products', 'Vouchers', 'No-show / cancel', 'Gross', ...(hasRf ? ['Refunds'] : []), 'Tax', 'Card fees', 'To business', 'Tips', 'Completed'].map(th)}
              </tr></thead>
              <tbody>
                {business.map(r => (
                  <tr key={r.staffId}>
                    {nameCell(r)}
                    <td style={{ ...detailStyles.td, whiteSpace: 'nowrap' }}>{typeLabel(r)}</td>
                    <td style={detailStyles.tdR}>{num(r.services)}</td>
                    <td style={detailStyles.tdR}>{num(r.products)}</td>
                    <td style={detailStyles.tdR}>{num(r.vouchers)}</td>
                    <td style={detailStyles.tdR}>{num(r.noShowCancelFees)}</td>
                    <td style={detailStyles.tdR}>{num(r.gross)}</td>
                    {hasRf && rfCell(r.refunds)}
                    <td style={detailStyles.tdR}>{num(r.tax || 0)}</td>
                    <td style={detailStyles.tdR}>{num(r.fees)}</td>
                    <td style={{ ...detailStyles.tdR, fontWeight: '600' }}>{num(r.toBusiness)}</td>
                    <td style={detailStyles.tdR}>{num(r.tips)}</td>
                    <td style={detailStyles.tdR}>{r.bookings}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                <td style={detailStyles.foot}>Total</td>
                <td style={detailStyles.foot}></td>
                <td style={detailStyles.footR}>{num(bt.services)}</td>
                <td style={detailStyles.footR}>{num(bt.products)}</td>
                <td style={detailStyles.footR}>{num(bt.vouchers)}</td>
                <td style={detailStyles.footR}>{num(bt.noShowCancelFees)}</td>
                <td style={detailStyles.footR}>{num(bt.gross)}</td>
                {hasRf && rfFoot(bt.refunds)}
                <td style={detailStyles.footR}>{num(taxTotal(bt, business))}</td>
                <td style={detailStyles.footR}>{num(bt.fees)}</td>
                <td style={detailStyles.footR}>{num(bt.toBusiness)}</td>
                <td style={detailStyles.footR}>{num(bt.tips)}</td>
                <td style={detailStyles.footR}>{bt.bookings || 0}</td>
              </tr></tfoot>
            </ScrollTable>
          </div>

          <div style={detailStyles.section}>
            <p style={detailStyles.sectionLabel}>Self-employed — gross is pass-through; only “to business” is salon turnover</p>
            {se.length ? (
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Staff</th>
                  {['Rate', 'Services', 'Products', 'Vouchers', 'No-show / cancel', 'Gross', ...(hasRf ? ['Refunds'] : []), 'Tax', 'Card fees', 'To business', 'To stylist', 'Via Beu', 'Owed in cash', 'Paid to salon', 'Tips', 'Completed'].map(th)}
                </tr></thead>
                <tbody>
                  {se.map(r => (
                    <tr key={r.staffId}>
                      {nameCell(r)}
                      <td style={{ ...detailStyles.tdR, whiteSpace: 'nowrap' }}>{serviceRateLabel(r)}{r.productRate ? ` / ${r.productRate}%` : ''}</td>
                      <td style={detailStyles.tdR}>{num(r.services)}</td>
                      <td style={detailStyles.tdR}>{num(r.products)}</td>
                      <td style={detailStyles.tdR}>{num(r.vouchers)}</td>
                      <td style={detailStyles.tdR}>{num(r.noShowCancelFees)}</td>
                      <td style={detailStyles.tdR}>{num(r.gross)}</td>
                      {hasRf && rfCell(r.refunds)}
                      <td style={detailStyles.tdR}>{num(r.tax || 0)}</td>
                      <td style={detailStyles.tdR}>{num(r.fees)}</td>
                      <td style={{ ...detailStyles.tdR, fontWeight: '600' }}>{num(r.toBusiness)}</td>
                      <td style={{ ...detailStyles.tdR, fontWeight: '600' }}>{num(r.toStylist)}</td>
                      <td style={detailStyles.tdR}>{num(r.settledViaBeu)}</td>
                      <td style={detailStyles.tdR}>{num(r.owedInCash)}</td>
                      <td style={detailStyles.tdR}>{num(r.paidToSalon)}</td>
                      <td style={detailStyles.tdR}>{num(r.tips)}</td>
                      <td style={detailStyles.tdR}>{r.bookings}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td style={detailStyles.foot}>Total</td>
                  <td style={detailStyles.foot}></td>
                  <td style={detailStyles.footR}>{num(st.services)}</td>
                  <td style={detailStyles.footR}>{num(st.products)}</td>
                  <td style={detailStyles.footR}>{num(st.vouchers)}</td>
                  <td style={detailStyles.footR}>{num(st.noShowCancelFees)}</td>
                  <td style={detailStyles.footR}>{num(st.gross)}</td>
                  {hasRf && rfFoot(st.refunds)}
                  <td style={detailStyles.footR}>{num(taxTotal(st, se))}</td>
                  <td style={detailStyles.footR}>{num(st.fees)}</td>
                  <td style={detailStyles.footR}>{num(st.toBusiness)}</td>
                  <td style={detailStyles.footR}>{num(st.toStylist)}</td>
                  <td style={detailStyles.footR}>{num(st.settledViaBeu)}</td>
                  <td style={detailStyles.footR}>{num(st.owedInCash)}</td>
                  <td style={detailStyles.footR}>{num(st.paidToSalon)}</td>
                  <td style={detailStyles.footR}>{num(st.tips)}</td>
                  <td style={detailStyles.footR}>{st.bookings || 0}</td>
                </tr></tfoot>
              </ScrollTable>
            ) : <EmptyState msg="No self-employed staff in this period" />}
          </div>

          {totalsBlock}
        </div>
      );
    }
    case 'commission-payroll': {
      const comm = data.commission || [];
      if (!comm.length) return <EmptyState msg="No commission data available" />;
      return (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionLabel}>Commission Breakdown</p>
          <ScrollTable>
            <thead><tr>
              <th style={detailStyles.th}>Staff Member</th>
              <th style={detailStyles.thR}>Service</th>
              <th style={detailStyles.thR}>Product</th>
              <th style={detailStyles.thR}>Total</th>
            </tr></thead>
            <tbody>
              {comm.map(c => (
                <tr key={c.hairdresserId || c.name}>
                  <td style={detailStyles.tdBold}>{c.name}</td>
                  <td style={detailStyles.tdR}>{fmt(c.serviceCommission)}</td>
                  <td style={detailStyles.tdR}>{fmt(c.productCommission)}</td>
                  <td style={{ ...detailStyles.tdR, fontWeight: '600' }}>{fmt(c.totalCommission)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td style={detailStyles.foot}>Total</td>
              <td style={detailStyles.footR}>{fmt(comm.reduce((s, c) => s + (c.serviceCommission || 0), 0))}</td>
              <td style={detailStyles.footR}>{fmt(comm.reduce((s, c) => s + (c.productCommission || 0), 0))}</td>
              <td style={detailStyles.footR}>{fmt(comm.reduce((s, c) => s + (c.totalCommission || 0), 0))}</td>
            </tr></tfoot>
          </ScrollTable>
        </div>
      );
    }
    case 'vat-report': {
      const t = data.totals || {};
      const byRate = data.byRate || [];
      const bySeller = data.bySeller || [];
      const byDomain = data.byDomain || [];
      const rf = data.refunded || {};
      const hasRef = rf.refunds > 0;
      if (!t.sales && !hasRef) return <EmptyState msg="No tax on sales in this period. Tax applies once it is switched on under Catalogue → Taxes." />;
      const bySite = Array.isArray(data.bySite) ? data.bySite : null;
      // Two extra columns only when refunds were made in the period.
      const refTh = hasRef ? <><th style={detailStyles.thR}>Refunded</th><th style={detailStyles.thR}>Due</th></> : null;
      const refTd = (r) => hasRef ? <><td style={detailStyles.tdR}>{fmt(-((r.refunded && r.refunded.tax) || 0))}</td><td style={detailStyles.tdR}>{fmt(r.due != null ? r.due : r.tax)}</td></> : null;
      const domainLabel = (d) => d === 'services' ? 'Services' : d === 'products' ? 'Products' : d === 'vouchers' ? 'Vouchers' : d === 'shipping' ? 'Delivery' : d;
      return (
        <>
          {bySite && (
            <div style={detailStyles.section}>
              <p style={detailStyles.sectionLabel}>By location</p>
              <ScrollTable>
                <thead><tr><th style={detailStyles.th}>Location</th><th style={detailStyles.thR}>Net</th><th style={detailStyles.thR}>Tax</th>{refTh}<th style={detailStyles.thR}>Gross</th><th style={detailStyles.thR}>Sales</th></tr></thead>
                <tbody>
                  {bySite.map(r => (
                    <tr key={r.salonId}><td style={detailStyles.tdBold}>{r.name}</td><td style={detailStyles.tdR}>{fmt(r.net)}</td><td style={detailStyles.tdR}>{fmt(r.tax)}</td>{refTd(r)}<td style={detailStyles.tdR}>{fmt(r.gross)}</td><td style={detailStyles.tdR}>{r.sales}</td></tr>
                  ))}
                </tbody>
              </ScrollTable>
            </div>
          )}
          <div style={detailStyles.section}>
            <p style={detailStyles.sectionLabel}>By rate</p>
            <ScrollTable>
              <thead><tr>
                <th style={detailStyles.th}>Rate</th>
                <th style={detailStyles.thR}>Net</th>
                <th style={detailStyles.thR}>Tax</th>
                {refTh}
                <th style={detailStyles.thR}>Gross</th>
                <th style={detailStyles.thR}>Lines</th>
              </tr></thead>
              <tbody>
                {byRate.map(r => (
                  <tr key={`${r.name}-${r.rate}`}>
                    <td style={detailStyles.tdBold}>{r.name} {r.rate}%</td>
                    <td style={detailStyles.tdR}>{fmt(r.net)}</td>
                    <td style={detailStyles.tdR}>{fmt(r.tax)}</td>
                    {refTd(r)}
                    <td style={detailStyles.tdR}>{fmt(r.gross)}</td>
                    <td style={detailStyles.tdR}>{r.lines}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                <td style={detailStyles.foot}>Total</td>
                <td style={detailStyles.footR}>{fmt(t.net)}</td>
                <td style={detailStyles.footR}>{fmt(t.tax)}</td>
                {hasRef && <><td style={detailStyles.footR}>{fmt(-(rf.tax || 0))}</td><td style={detailStyles.footR}>{fmt(data.due)}</td></>}
                <td style={detailStyles.footR}>{fmt(t.gross)}</td>
                <td style={detailStyles.footR}>{t.sales} sales</td>
              </tr></tfoot>
            </ScrollTable>
          </div>
          <div style={detailStyles.section}>
            <p style={detailStyles.sectionLabel}>By seller</p>
            <ScrollTable>
              <thead><tr>
                <th style={detailStyles.th}>Seller</th>
                <th style={detailStyles.th}>VAT no.</th>
                <th style={detailStyles.thR}>Net</th>
                <th style={detailStyles.thR}>Tax</th>
                {refTh}
                <th style={detailStyles.thR}>Gross</th>
                <th style={detailStyles.thR}>Sales</th>
              </tr></thead>
              <tbody>
                {bySeller.map(s => (
                  <tr key={s.sellerUserId || s.name}>
                    <td style={detailStyles.tdBold}>{s.name}</td>
                    <td style={detailStyles.td}>{s.taxNumber || '—'}</td>
                    <td style={detailStyles.tdR}>{fmt(s.net)}</td>
                    <td style={detailStyles.tdR}>{fmt(s.tax)}</td>
                    {refTd(s)}
                    <td style={detailStyles.tdR}>{fmt(s.gross)}</td>
                    <td style={detailStyles.tdR}>{s.sales}</td>
                  </tr>
                ))}
              </tbody>
            </ScrollTable>
            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '10px 0 0 0', lineHeight: 1.4 }}>Each seller's tax is their own liability: the salon for its own and employed staff's sales, a self-employed stylist for theirs. Figures are frozen at the time of each sale, so later rate changes do not move them.{hasRef ? ' Refunds count in the period they were made, not the period of the sale.' : ''}</p>
          </div>
          <div style={detailStyles.section}>
            <p style={detailStyles.sectionLabel}>By type</p>
            <ScrollTable>
              <thead><tr>
                <th style={detailStyles.th}>Type</th>
                <th style={detailStyles.thR}>Net</th>
                <th style={detailStyles.thR}>Tax</th>
                {refTh}
                <th style={detailStyles.thR}>Gross</th>
              </tr></thead>
              <tbody>
                {byDomain.map(d => (
                  <tr key={d.domain}>
                    <td style={detailStyles.tdBold}>{domainLabel(d.domain)}</td>
                    <td style={detailStyles.tdR}>{fmt(d.net)}</td>
                    <td style={detailStyles.tdR}>{fmt(d.tax)}</td>
                    {refTd(d)}
                    <td style={detailStyles.tdR}>{fmt(d.gross)}</td>
                  </tr>
                ))}
              </tbody>
            </ScrollTable>
          </div>
        </>
      );
    }
    case 'manual-discounts': {
      const m = data.manual || {};
      const sales = m.sales || [];
      const codes = (data.summary && data.summary.codes) || { byCode: [] };
      if (!sales.length && !(codes.byCode || []).length) return <EmptyState msg="No discounts were given in this period" />;
      const lineText = (l) => `${l.name || (l.kind === 'product' ? 'Product' : 'Service')}${l.qty > 1 ? ` × ${l.qty}` : ''}: ${fmt(manualFrom(l))} → ${fmt(l.charged)}`;
      const FUND = { salon: 'Salon pays', stylist: 'Stylist pays', shared: 'Shared' };
      return (
        <>
          {(codes.byCode || []).length > 0 && (
            <div style={detailStyles.section}>
              <p style={detailStyles.sectionLabel}>Discount codes</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Code</th>
                  <th style={detailStyles.th}>Who pays</th>
                  <th style={detailStyles.thR}>Uses</th>
                  <th style={detailStyles.thR}>Taken off</th>
                </tr></thead>
                <tbody>
                  {codes.byCode.map(c => (
                    <tr key={`${c.code}-${c.fundingMode}`}>
                      <td style={detailStyles.tdBold}>{c.code}</td>
                      <td style={detailStyles.td}>{FUND[c.fundingMode] || ''}</td>
                      <td style={detailStyles.tdR}>{c.count}</td>
                      <td style={detailStyles.tdR}>{fmt(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td style={detailStyles.foot}>Total</td>
                  <td style={detailStyles.foot}>Salon {fmt(codes.salonAbsorbed || 0)} · stylists {fmt(codes.stylistAbsorbed || 0)}</td>
                  <td style={detailStyles.footR}>{codes.count || 0}</td>
                  <td style={detailStyles.footR}>{fmt(codes.total || 0)}</td>
                </tr></tfoot>
              </ScrollTable>
            </div>
          )}
          {sales.length > 0 && <>
          <div style={detailStyles.section}>
            <p style={detailStyles.sectionLabel}>Taken off by hand: by who rang it up</p>
            <ScrollTable>
              <thead><tr>
                <th style={detailStyles.th}>Staff Member</th>
                <th style={detailStyles.thR}>Taken off</th>
                <th style={detailStyles.thR}>Sales</th>
                <th style={detailStyles.thR}>Without Discounts</th>
              </tr></thead>
              <tbody>
                {(m.byStaff || []).map(k => (
                  <tr key={k.userId || k.name}>
                    <td style={detailStyles.tdBold}>{k.name}</td>
                    <td style={detailStyles.tdR}>{fmt(k.amount)}</td>
                    <td style={detailStyles.tdR}>{k.sales}</td>
                    <td style={detailStyles.tdR}>{k.withoutRight || 0}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                <td style={detailStyles.foot}>Total</td>
                <td style={detailStyles.footR}>{fmt((m.totals || {}).amount || 0)}</td>
                <td style={detailStyles.footR}>{(m.totals || {}).sales || 0}</td>
                <td style={detailStyles.footR}>{(m.totals || {}).withoutRight || 0}</td>
              </tr></tfoot>
            </ScrollTable>
          </div>
          <div style={detailStyles.section}>
            <p style={detailStyles.sectionLabel}>Each sale</p>
            <ScrollTable>
              <thead><tr>
                <th style={detailStyles.th}>Date</th>
                <th style={detailStyles.th}>Rang up</th>
                <th style={detailStyles.th}>Stylist</th>
                <th style={detailStyles.th}>What changed</th>
                <th style={detailStyles.thR}>Taken off</th>
              </tr></thead>
              <tbody>
                {sales.map(x => (
                  <tr key={x.bookingId}>
                    <td style={detailStyles.td}>{fmtDate(x.date)}</td>
                    <td style={detailStyles.tdBold}>{x.byName || 'Unknown'}{!x.hadRight ? <span style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: '#b45309' }}>{x.overrode ? 'No Discounts · card already taken' : 'No Discounts'}</span> : null}</td>
                    <td style={detailStyles.td}>{x.stylistName || ''}</td>
                    <td style={detailStyles.td}>{(x.lines || []).map((l, i) => <span key={i} style={{ display: 'block' }}>{lineText(l)}</span>)}</td>
                    <td style={detailStyles.tdR}>{fmt(x.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </ScrollTable>
            {m.truncated ? <p style={{ fontSize: '11px', color: '#9ca3af', margin: '8px 0 0 0' }}>Showing the latest 500 sales. Pick a shorter period to see the rest.</p> : null}
            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '8px 0 0 0', lineHeight: 1.4 }}>Measured against the menu price at the time of the sale. Where a code was used too, the price shown is after the code; the code’s saving is counted under Discount codes.</p>
          </div>
          </>}
        </>
      );
    }
    case 'tips-report': {
      const tips = data.tips || [];
      if (!tips.length) return <EmptyState msg="No tips data available" />;
      return (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionLabel}>Tips by Staff</p>
          <ScrollTable>
            <thead><tr>
              <th style={detailStyles.th}>Staff Member</th>
              <th style={detailStyles.thR}>Total Tips</th>
              <th style={detailStyles.thR}>Bookings</th>
              <th style={detailStyles.thR}>Avg. Tip</th>
            </tr></thead>
            <tbody>
              {tips.map(t => (
                <tr key={t.hairdresserId || t.name}>
                  <td style={detailStyles.tdBold}>{t.name}</td>
                  <td style={detailStyles.tdR}>{fmt(t.totalTips)}</td>
                  <td style={detailStyles.tdR}>{t.tipCount}</td>
                  <td style={detailStyles.tdR}>{fmt(t.averageTip || 0)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td style={detailStyles.foot}>Total</td>
              <td style={detailStyles.footR}>{fmt(tips.reduce((s, t) => s + (t.totalTips || 0), 0))}</td>
              <td style={detailStyles.footR}>{tips.reduce((s, t) => s + (t.tipCount || 0), 0)}</td>
              <td style={detailStyles.footR}>{fmt(tips.reduce((s, t) => s + (t.totalTips || 0), 0) / Math.max(tips.reduce((s, t) => s + (t.tipCount || 0), 0), 1))}</td>
            </tr></tfoot>
          </ScrollTable>
        </div>
      );
    }
    case 'rebook-rate': {
      const r = data.rebooking || {};
      const pct = r.value || 0;
      const staffRebook = data.byStaff || [];
      return (
        <div style={detailStyles.section}>
          <div style={{ padding: '20px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: '12px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>Rebooking Rate</span>
              <span style={{ fontSize: '28px', fontWeight: '700', color: pct >= 60 ? '#16a34a' : pct >= 40 ? '#f59e0b' : '#dc2626' }}>{pct}%</span>
            </div>
            <div style={{ height: '8px', borderRadius: '4px', backgroundColor: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', borderRadius: '4px', background: pct >= 60 ? 'linear-gradient(90deg, #16a34a, #22c55e)' : pct >= 40 ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' : 'linear-gradient(90deg, #dc2626, #f87171)', transition: 'width 0.6s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>{r.rebooked || 0} rebooked</span>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>{r.total || 0} total</span>
            </div>
            <div style={{ textAlign: 'center', marginTop: '6px' }}>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>Window: {rebookThreshold && rebookThreshold < 168 ? `${rebookThreshold} hours` : rebookThreshold === 168 ? '7 days' : '48 hours'}</span>
            </div>
          </div>
          {staffRebook.length > 0 && (
            <>
              <p style={detailStyles.sectionLabel}>Rebook Rate by Staff</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Staff</th>
                  <th style={{ ...detailStyles.th, width: '100px' }}>Rate</th>
                  <th style={detailStyles.thR}>Rebooked</th>
                  <th style={detailStyles.thR}>Total</th>
                </tr></thead>
                <tbody>
                  {staffRebook.map((s, i) => {
                    const sp = s.rate || 0;
                    return (
                      <tr key={i}>
                        <td style={detailStyles.tdBold}>{s.name}</td>
                        <td style={detailStyles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={detailStyles.barBg}><div style={{ ...detailStyles.bar(sp), background: sp >= 60 ? '#16a34a' : sp >= 40 ? '#f59e0b' : '#dc2626' }} /></div>
                            <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '28px' }}>{sp}%</span>
                          </div>
                        </td>
                        <td style={detailStyles.tdR}>{s.rebooked}</td>
                        <td style={detailStyles.tdR}>{s.total}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </ScrollTable>
            </>
          )}
        </div>
      );
    }
    case 'client-retention': {
      const o = data.overall || {};
      const pct = o.rate || 0;
      const staffRetention = data.byStaff || [];
      const clients = data.clients || [];
      return (
        <div style={detailStyles.section}>
          <div style={{ padding: '20px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: '12px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>Client Retention</span>
              <span style={{ fontSize: '28px', fontWeight: '700', color: pct >= 60 ? '#16a34a' : pct >= 40 ? '#f59e0b' : '#dc2626' }}>{pct}%</span>
            </div>
            <div style={{ height: '8px', borderRadius: '4px', backgroundColor: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', borderRadius: '4px', background: pct >= 60 ? 'linear-gradient(90deg, #16a34a, #22c55e)' : pct >= 40 ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' : 'linear-gradient(90deg, #dc2626, #f87171)', transition: 'width 0.6s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>{o.retainedClients || 0} retained</span>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>{o.lostClients || 0} lost</span>
            </div>
            <div style={{ textAlign: 'center', marginTop: '6px' }}>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>Clients who booked again within {retentionThreshold || 90} days</span>
            </div>
          </div>
          {staffRetention.length > 0 && (
            <>
              <p style={detailStyles.sectionLabel}>Retention by Staff</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Staff</th>
                  <th style={{ ...detailStyles.th, width: '100px' }}>Rate</th>
                  <th style={detailStyles.thR}>Retained</th>
                  <th style={detailStyles.thR}>Clients</th>
                </tr></thead>
                <tbody>
                  {staffRetention.map((s, i) => {
                    const sp = s.rate || 0;
                    return (
                      <tr key={i}>
                        <td style={detailStyles.tdBold}>{s.name}</td>
                        <td style={detailStyles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={detailStyles.barBg}><div style={{ ...detailStyles.bar(sp), background: sp >= 60 ? '#16a34a' : sp >= 40 ? '#f59e0b' : '#dc2626' }} /></div>
                            <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '28px' }}>{sp}%</span>
                          </div>
                        </td>
                        <td style={detailStyles.tdR}>{s.retainedClients}</td>
                        <td style={detailStyles.tdR}>{s.totalClients}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </ScrollTable>
            </>
          )}
          {clients.length > 0 && (
            <>
              <p style={detailStyles.sectionLabel}>Client Breakdown</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Client</th>
                  <th style={detailStyles.th}>Status</th>
                  <th style={detailStyles.thR}>Visits</th>
                  <th style={detailStyles.thR}>Spent</th>
                </tr></thead>
                <tbody>
                  {clients.map((c, i) => (
                    <tr key={i}>
                      <td style={detailStyles.tdBold}>{c.name}</td>
                      <td style={detailStyles.td}><span style={detailStyles.pill(c.retained ? 'green' : 'red')}>{c.retained ? 'Retained' : 'Lost'}</span></td>
                      <td style={detailStyles.tdR}>{c.visits}</td>
                      <td style={detailStyles.tdR}>{fmt(c.totalSpent)}</td>
                    </tr>
                  ))}
                </tbody>
              </ScrollTable>
            </>
          )}
        </div>
      );
    }
    case 'new-vs-returning': {
      const r = data.retention || {};
      const nc = data.newClients || [];
      const clients = data.clients || [];
      const total = (r.newClients || 0) + (r.returningClients || 0);
      const newPct = total > 0 ? Math.round((r.newClients || 0) / total * 100) : 0;
      return (
        <div style={detailStyles.section}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1, padding: '16px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: '12px', textAlign: 'center' }}>
              <p style={{ fontSize: '28px', fontWeight: '700', color: '#007EA0', margin: '0 0 4px 0' }}>{r.newClients || 0}</p>
              <p style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', margin: 0 }}>New Clients {total > 0 && <span style={{ color: '#007EA0' }}>({newPct}%)</span>}</p>
            </div>
            <div style={{ flex: 1, padding: '16px', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderRadius: '12px', textAlign: 'center' }}>
              <p style={{ fontSize: '28px', fontWeight: '700', color: '#16a34a', margin: '0 0 4px 0' }}>{r.returningClients || 0}</p>
              <p style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', margin: 0 }}>Returning {total > 0 && <span style={{ color: '#16a34a' }}>({100 - newPct}%)</span>}</p>
            </div>
            <div style={{ flex: 1, padding: '16px', background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)', borderRadius: '12px', textAlign: 'center' }}>
              <p style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: '0 0 4px 0' }}>{fmt(r.avgRevenuePerClient)}</p>
              <p style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', margin: 0 }}>Avg Spend</p>
            </div>
          </div>
          {total > 0 && (
            <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden', marginBottom: '16px' }}>
              <div style={{ width: `${newPct}%`, backgroundColor: '#007EA0', transition: 'width 0.4s ease' }} />
              <div style={{ flex: 1, backgroundColor: '#16a34a' }} />
            </div>
          )}
          <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: '10px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
            <p style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0' }}>How we classify clients</p>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#007EA0', marginTop: '4px', flexShrink: 0 }} />
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: '1.4' }}>
                  <strong style={{ color: '#007EA0' }}>New</strong> — First-ever completed visit at your salon falls within this period
                </p>
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#16a34a', marginTop: '4px', flexShrink: 0 }} />
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: '1.4' }}>
                  <strong style={{ color: '#16a34a' }}>Returning</strong> — Had a previous completed visit before the start of this period
                </p>
              </div>
            </div>
          </div>
          {clients.length > 0 && (
            <>
              <p style={detailStyles.sectionLabel}>Client Breakdown</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Client</th>
                  <th style={detailStyles.th}>Type</th>
                  <th style={detailStyles.thR}>Visits</th>
                  <th style={detailStyles.thR}>Spent</th>
                </tr></thead>
                <tbody>
                  {clients.map((c, i) => (
                    <tr key={i}>
                      <td style={detailStyles.tdBold}>{c.name}</td>
                      <td style={detailStyles.td}><span style={detailStyles.pill(c.isNew ? 'blue' : 'green')}>{c.isNew ? 'New' : 'Returning'}</span></td>
                      <td style={detailStyles.tdR}>{c.visits}</td>
                      <td style={detailStyles.tdR}>{fmt(c.totalSpent)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td style={detailStyles.foot} colSpan={2}>Total ({clients.length} clients)</td>
                  <td style={detailStyles.footR}>{clients.reduce((s, c) => s + (c.visits || 0), 0)}</td>
                  <td style={detailStyles.footR}>{fmt(clients.reduce((s, c) => s + (c.totalSpent || 0), 0))}</td>
                </tr></tfoot>
              </ScrollTable>
            </>
          )}
          {nc.length > 0 && (
            <>
              <p style={detailStyles.sectionLabel}>New Clients</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Client</th>
                  <th style={detailStyles.th}>Source</th>
                  <th style={detailStyles.th}>Staff</th>
                  <th style={detailStyles.thR}>Spent</th>
                </tr></thead>
                <tbody>
                  {nc.map((c, i) => (
                    <tr key={i}>
                      <td style={detailStyles.tdBold}>{c.name || 'Unknown'}</td>
                      <td style={detailStyles.td}><span style={detailStyles.pill(c.source === 'Online' ? 'blue' : 'green')}>{c.source || 'In Store'}</span></td>
                      <td style={detailStyles.td}>{c.staff || '-'}</td>
                      <td style={detailStyles.tdR}>{fmt(c.totalSpent)}</td>
                    </tr>
                  ))}
                </tbody>
              </ScrollTable>
            </>
          )}
          {clients.length === 0 && nc.length === 0 && (
            <p style={detailStyles.empty}>No client data for this period</p>
          )}
        </div>
      );
    }
    case 'payment-methods': {
      const bd = data.breakdown || [];
      if (!bd.length) return <EmptyState msg="No payment data available" />;
      const total = bd.reduce((s, b) => s + (b.total || 0), 0);
      const bdRefunds = bd.some((b) => rowRefunds(b));
      return (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionLabel}>Payment Methods</p>
          <ScrollTable>
            <thead><tr>
              <th style={detailStyles.th}>Method</th>
              <th style={{ ...detailStyles.th, width: '100px' }}>Share</th>
              <th style={detailStyles.thR}>Transactions</th>
              {bdRefunds && <th style={detailStyles.thR}>Refunded</th>}
              <th style={detailStyles.thR}>Total</th>
            </tr></thead>
            <tbody>
              {bd.map((b, i) => {
                const pct = total > 0 ? Math.round((b.total || 0) / total * 100) : 0;
                const rf = rowRefunds(b);
                return (
                  <tr key={i}>
                    <td style={detailStyles.tdBold}>{b.paymentName || b.paymentType || b._id || 'Unknown'}</td>
                    <td style={detailStyles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={detailStyles.barBg}><div style={detailStyles.bar(pct)} /></div>
                        <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '28px' }}>{pct}%</span>
                      </div>
                    </td>
                    <td style={detailStyles.tdR}>{b.count}</td>
                    {bdRefunds && <td style={{ ...detailStyles.tdR, color: rf ? '#b42318' : undefined }}>{rf ? minusMoney(rf.amount) : '£0'}</td>}
                    <td style={detailStyles.tdR}>{fmt(b.total)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr>
              <td style={detailStyles.foot} colSpan={2}>Total</td>
              <td style={detailStyles.footR}>{bd.reduce((s, b) => s + (b.count || 0), 0)}</td>
              {bdRefunds && <td style={detailStyles.footR}>{minusMoney(bd.reduce((s, b) => s + ((rowRefunds(b) || {}).amount || 0), 0))}</td>}
              <td style={detailStyles.footR}>{fmt(total)}</td>
            </tr></tfoot>
          </ScrollTable>
        </div>
      );
    }
    case 'product-sales': {
      const products = data.products || [];
      const productByStaff = data.byStaff || [];
      if (!products.length) return <EmptyState msg="No product sales in this period" />;
      const staffTotal = productByStaff.reduce((s, st) => s + (st.totalRevenue || 0), 0);
      return (
        <div style={detailStyles.section}>
          {productByStaff.length > 1 && (
            <>
              <p style={detailStyles.sectionLabel}>By Staff</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Staff</th>
                  <th style={{ ...detailStyles.th, width: '100px' }}>Share</th>
                  <th style={detailStyles.thR}>Items</th>
                  <th style={detailStyles.thR}>Revenue</th>
                </tr></thead>
                <tbody>
                  {productByStaff.map((s, i) => {
                    const pct = staffTotal > 0 ? Math.round((s.totalRevenue || 0) / staffTotal * 100) : 0;
                    return (
                      <tr key={i}>
                        <td style={detailStyles.tdBold}>{s.name}</td>
                        <td style={detailStyles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={detailStyles.barBg}><div style={detailStyles.bar(pct)} /></div>
                            <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '28px' }}>{pct}%</span>
                          </div>
                        </td>
                        <td style={detailStyles.tdR}>{s.totalQuantity}</td>
                        <td style={detailStyles.tdR}>{fmt(s.totalRevenue)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr>
                  <td style={detailStyles.foot} colSpan={2}>Total</td>
                  <td style={detailStyles.footR}>{productByStaff.reduce((s, st) => s + (st.totalQuantity || 0), 0)}</td>
                  <td style={detailStyles.footR}>{fmt(staffTotal)}</td>
                </tr></tfoot>
              </ScrollTable>
            </>
          )}
          <p style={detailStyles.sectionLabel}>Product Sales</p>
          <ScrollTable>
            <thead><tr>
              <th style={detailStyles.th}>Product</th>
              <th style={detailStyles.thR}>Qty Sold</th>
              <th style={detailStyles.thR}>Revenue</th>
              <th style={detailStyles.thR}>Avg. Price</th>
            </tr></thead>
            <tbody>
              {products.map((p, i) => (
                <tr key={i}>
                  <td style={detailStyles.tdBold}>{p.productName || p.name}</td>
                  <td style={detailStyles.tdR}>{p.totalQuantity}</td>
                  <td style={detailStyles.tdR}>{fmt(p.totalRevenue)}</td>
                  <td style={detailStyles.tdR}>{fmt(p.avgPrice)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td style={detailStyles.foot}>Total</td>
              <td style={detailStyles.footR}>{products.reduce((s, p) => s + (p.totalQuantity || 0), 0)}</td>
              <td style={detailStyles.footR}>{fmt(products.reduce((s, p) => s + (p.totalRevenue || 0), 0))}</td>
              <td style={detailStyles.footR}>-</td>
            </tr></tfoot>
          </ScrollTable>
        </div>
      );
    }
    case 'inventory-report': {
      const items = data.inventory?.items || [];
      const last = data.inventory?.lastStocktake;
      if (!items.length) return <EmptyState msg="No products found" />;
      return (
        <div style={detailStyles.section}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <p style={detailStyles.sectionLabel}>Inventory</p>
            {last && <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>Last stocktake: {new Date(last.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
          </div>
          <ScrollTable>
            <thead><tr>
              <th style={detailStyles.th}>Product</th>
              <th style={detailStyles.th}>Category</th>
              <th style={detailStyles.thR}>Qty</th>
              <th style={detailStyles.thR}>Cost</th>
              <th style={detailStyles.thR}>Retail</th>
            </tr></thead>
            <tbody>
              {items.map((p, i) => (
                <tr key={i}>
                  <td style={detailStyles.tdBold}>{p.name}</td>
                  <td style={detailStyles.td}>{p.category}</td>
                  <td style={detailStyles.tdR}>
                    {p.quantity <= 0
                      ? <span style={detailStyles.pill('red')}>{p.quantity}</span>
                      : p.quantity
                    }
                  </td>
                  <td style={detailStyles.tdR}>{fmt(p.stockValue)}</td>
                  <td style={detailStyles.tdR}>{fmt(p.retailValue)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td style={detailStyles.foot} colSpan={2}>Total</td>
              <td style={detailStyles.footR}>{items.reduce((s, p) => s + (p.quantity || 0), 0)}</td>
              <td style={detailStyles.footR}>{fmt(items.reduce((s, p) => s + (p.stockValue || 0), 0))}</td>
              <td style={detailStyles.footR}>{fmt(items.reduce((s, p) => s + (p.retailValue || 0), 0))}</td>
            </tr></tfoot>
          </ScrollTable>
        </div>
      );
    }
    case 'gift-vouchers': {
      const v = data.vouchers || {};
      const soldItems = v.sold?.breakdown || [];
      const voucherByStaff = data.byStaff || [];
      const outstanding = Math.round(((v.sold?.total || 0) - (v.redeemed?.total || 0)) * 100) / 100;
      const vStaffTotal = voucherByStaff.reduce((s, st) => s + (st.totalRevenue || 0), 0);
      return (
        <div style={detailStyles.section}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div style={{ flex: 1, padding: '18px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: '12px' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0' }}>Sold</p>
              <p style={{ fontSize: '24px', fontWeight: '700', color: '#007EA0', margin: '0 0 4px 0' }}>{fmt(v.sold?.total)}</p>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{v.sold?.count || 0} vouchers</p>
            </div>
            <div style={{ flex: 1, padding: '18px', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderRadius: '12px' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0' }}>Redeemed</p>
              <p style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a', margin: '0 0 4px 0' }}>{fmt(v.redeemed?.total)}</p>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{v.redeemed?.count || 0} vouchers</p>
            </div>
            <div style={{ flex: 1, padding: '18px', background: outstanding > 0 ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)', borderRadius: '12px' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0' }}>Outstanding</p>
              <p style={{ fontSize: '24px', fontWeight: '700', color: outstanding > 0 ? '#d97706' : '#6b7280', margin: '0 0 4px 0' }}>{fmt(outstanding)}</p>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>liability</p>
            </div>
          </div>
          {voucherByStaff.length > 1 && (
            <>
              <p style={detailStyles.sectionLabel}>By Staff</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Staff</th>
                  <th style={{ ...detailStyles.th, width: '100px' }}>Share</th>
                  <th style={detailStyles.thR}>Sales</th>
                  <th style={detailStyles.thR}>Revenue</th>
                </tr></thead>
                <tbody>
                  {voucherByStaff.map((s, i) => {
                    const pct = vStaffTotal > 0 ? Math.round((s.totalRevenue || 0) / vStaffTotal * 100) : 0;
                    return (
                      <tr key={i}>
                        <td style={detailStyles.tdBold}>{s.name}</td>
                        <td style={detailStyles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={detailStyles.barBg}><div style={detailStyles.bar(pct)} /></div>
                            <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '28px' }}>{pct}%</span>
                          </div>
                        </td>
                        <td style={detailStyles.tdR}>{s.count}</td>
                        <td style={detailStyles.tdR}>{fmt(s.totalRevenue)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr>
                  <td style={detailStyles.foot} colSpan={2}>Total</td>
                  <td style={detailStyles.footR}>{voucherByStaff.reduce((s, st) => s + (st.count || 0), 0)}</td>
                  <td style={detailStyles.footR}>{fmt(vStaffTotal)}</td>
                </tr></tfoot>
              </ScrollTable>
            </>
          )}
          {soldItems.length > 0 && (
            <>
              <p style={detailStyles.sectionLabel}>Vouchers Sold</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Voucher</th>
                  <th style={{ ...detailStyles.th, width: '100px' }}>Share</th>
                  <th style={detailStyles.thR}>Qty</th>
                  <th style={detailStyles.thR}>Total</th>
                </tr></thead>
                <tbody>
                  {soldItems.map((s, i) => {
                    const pct = (v.sold?.total || 0) > 0 ? Math.round((s.total || 0) / v.sold.total * 100) : 0;
                    return (
                      <tr key={i}>
                        <td style={detailStyles.tdBold}>{s.name}</td>
                        <td style={detailStyles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={detailStyles.barBg}><div style={detailStyles.bar(pct)} /></div>
                            <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '28px' }}>{pct}%</span>
                          </div>
                        </td>
                        <td style={detailStyles.tdR}>{s.count}</td>
                        <td style={detailStyles.tdR}>{fmt(s.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr>
                  <td style={detailStyles.foot} colSpan={2}>Total Sold</td>
                  <td style={detailStyles.footR}>{v.sold?.count || 0}</td>
                  <td style={detailStyles.footR}>{fmt(v.sold?.total)}</td>
                </tr></tfoot>
              </ScrollTable>
            </>
          )}
          {(v.redeemed?.count || 0) > 0 && (
            <>
              <p style={detailStyles.sectionLabel}>Redemptions</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Type</th>
                  <th style={detailStyles.thR}>Count</th>
                </tr></thead>
                <tbody>
                  <tr>
                    <td style={detailStyles.td}>Full redemption</td>
                    <td style={detailStyles.tdR}>{v.redeemed?.fullRedemptions || 0}</td>
                  </tr>
                  <tr>
                    <td style={detailStyles.td}>Partial redemption</td>
                    <td style={detailStyles.tdR}>{v.redeemed?.partialRedemptions || 0}</td>
                  </tr>
                </tbody>
                <tfoot><tr>
                  <td style={detailStyles.foot}>Total Redeemed</td>
                  <td style={detailStyles.footR}>{fmt(v.redeemed?.total)}</td>
                </tr></tfoot>
              </ScrollTable>
            </>
          )}
          {soldItems.length === 0 && (v.redeemed?.count || 0) === 0 && (
            <p style={detailStyles.empty}>No voucher activity in this period</p>
          )}
        </div>
      );
    }
    case 'loyalty': {
      const l = data.loyalty || {};
      const redeemed = l.redeemed || {};
      const funding = redeemed.funding || {};
      const salonFund = funding.salon || { count: 0, total: 0 };
      const stylistFund = funding.stylist || { count: 0, total: 0 };
      const breakdown = redeemed.breakdown || [];
      const liability = l.liability || {};
      const byStaff = [...(l.byStaff || [])].sort((a, b) => (b.total || 0) - (a.total || 0));
      const LOYALTY_ACCENT = '#ec4899';
      const hasActivity = (redeemed.count || 0) > 0 || (liability.bankedRewards || 0) > 0;
      if (!hasActivity) return <EmptyState msg="No loyalty activity in this period" />;
      const fundingTotal = (salonFund.total || 0) + (stylistFund.total || 0);
      return (
        <div style={detailStyles.section}>
          {/* Headline cards: redeemed value + banked liability */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div style={{ flex: 1, padding: '18px', background: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)', borderRadius: '12px' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0' }}>Rewards Redeemed</p>
              <p style={{ fontSize: '24px', fontWeight: '700', color: LOYALTY_ACCENT, margin: '0 0 4px 0' }}>{fmt(redeemed.total)}</p>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{redeemed.count || 0} reward{(redeemed.count || 0) !== 1 ? 's' : ''}</p>
            </div>
            <div style={{ flex: 1, padding: '18px', background: (liability.bankedRewards || 0) > 0 ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)', borderRadius: '12px' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0' }}>Banked Rewards Owed</p>
              <p style={{ fontSize: '24px', fontWeight: '700', color: (liability.bankedRewards || 0) > 0 ? '#d97706' : '#6b7280', margin: '0 0 4px 0' }}>{liability.bankedRewards || 0}</p>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{liability.clientsWithRewards || 0} client{(liability.clientsWithRewards || 0) !== 1 ? 's' : ''} with a reward ready</p>
            </div>
          </div>

          {/* Salon vs stylist funding split */}
          {fundingTotal > 0 && (
            <>
              <p style={detailStyles.sectionLabel}>Funded By</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Source</th>
                  <th style={detailStyles.thR}>Rewards</th>
                  <th style={detailStyles.thR}>Value</th>
                </tr></thead>
                <tbody>
                  <tr>
                    <td style={detailStyles.tdBold}>Salon-funded</td>
                    <td style={detailStyles.tdR}>{salonFund.count || 0}</td>
                    <td style={detailStyles.tdR}>{fmt(salonFund.total)}</td>
                  </tr>
                  <tr>
                    <td style={detailStyles.tdBold}>Stylist-funded</td>
                    <td style={detailStyles.tdR}>{stylistFund.count || 0}</td>
                    <td style={detailStyles.tdR}>{fmt(stylistFund.total)}</td>
                  </tr>
                </tbody>
                <tfoot><tr>
                  <td style={detailStyles.foot}>Total</td>
                  <td style={detailStyles.footR}>{(salonFund.count || 0) + (stylistFund.count || 0)}</td>
                  <td style={detailStyles.footR}>{fmt(fundingTotal)}</td>
                </tr></tfoot>
              </ScrollTable>
            </>
          )}

          {/* Per-scheme breakdown */}
          {breakdown.length > 0 && (
            <>
              <p style={detailStyles.sectionLabel}>By Scheme</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Scheme</th>
                  <th style={detailStyles.thR}>Redeemed</th>
                  <th style={detailStyles.thR}>Value</th>
                </tr></thead>
                <tbody>
                  {breakdown.map((s, i) => (
                    <tr key={s.schemeId || i}>
                      <td style={detailStyles.tdBold}>{s.name || 'Scheme'}</td>
                      <td style={detailStyles.tdR}>{s.count || 0}</td>
                      <td style={detailStyles.tdR}>{fmt(s.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td style={detailStyles.foot}>Total</td>
                  <td style={detailStyles.footR}>{breakdown.reduce((s, b) => s + (b.count || 0), 0)}</td>
                  <td style={detailStyles.footR}>{fmt(breakdown.reduce((s, b) => s + (b.total || 0), 0))}</td>
                </tr></tfoot>
              </ScrollTable>
            </>
          )}

          {/* Per-stylist staff table */}
          {byStaff.length > 0 && (
            <>
              <p style={detailStyles.sectionLabel}>Rewards Redeemed By Staff</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Staff</th>
                  <th style={detailStyles.thR}>Rewards</th>
                  <th style={detailStyles.thR}>Salon / Stylist</th>
                  <th style={detailStyles.thR}>Value</th>
                </tr></thead>
                <tbody>
                  {byStaff.map((s, i) => (
                    <tr key={s.hairdresserId || s.name || i}>
                      <td style={detailStyles.tdBold}>{s.name || 'Unknown'}</td>
                      <td style={detailStyles.tdR}>{s.count || 0}</td>
                      <td style={detailStyles.tdR}>{s.salonFundedCount || 0} / {s.stylistFundedCount || 0}</td>
                      <td style={detailStyles.tdR}>{fmt(s.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td style={detailStyles.foot}>Total</td>
                  <td style={detailStyles.footR}>{byStaff.reduce((s, st) => s + (st.count || 0), 0)}</td>
                  <td style={detailStyles.footR}>{byStaff.reduce((s, st) => s + (st.salonFundedCount || 0), 0)} / {byStaff.reduce((s, st) => s + (st.stylistFundedCount || 0), 0)}</td>
                  <td style={detailStyles.footR}>{fmt(byStaff.reduce((s, st) => s + (st.total || 0), 0))}</td>
                </tr></tfoot>
              </ScrollTable>
            </>
          )}
        </div>
      );
    }
    case 'revenue-summary': {
      const bd = data.paymentBreakdown || [];
      if (!bd.length) return null;
      const total = bd.reduce((s, b) => s + (b.total || 0), 0);
      const bdRefunds = bd.some((b) => rowRefunds(b));
      return (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionLabel}>Revenue by Payment Method</p>
          <ScrollTable>
            <thead><tr>
              <th style={detailStyles.th}>Method</th>
              <th style={{ ...detailStyles.th, width: '100px' }}>Share</th>
              <th style={detailStyles.thR}>Transactions</th>
              {bdRefunds && <th style={detailStyles.thR}>Refunded</th>}
              <th style={detailStyles.thR}>Total</th>
            </tr></thead>
            <tbody>
              {bd.map((b, i) => {
                const pct = total > 0 ? Math.round((b.total || 0) / total * 100) : 0;
                const rf = rowRefunds(b);
                return (
                  <tr key={i}>
                    <td style={detailStyles.tdBold}>{b.paymentName || b.paymentType || b._id || 'Unknown'}</td>
                    <td style={detailStyles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={detailStyles.barBg}><div style={detailStyles.bar(pct)} /></div>
                        <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '28px' }}>{pct}%</span>
                      </div>
                    </td>
                    <td style={detailStyles.tdR}>{b.count}</td>
                    {bdRefunds && <td style={{ ...detailStyles.tdR, color: rf ? '#b42318' : undefined }}>{rf ? minusMoney(rf.amount) : '£0'}</td>}
                    <td style={detailStyles.tdR}>{fmt(b.total)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr>
              <td style={detailStyles.foot} colSpan={2}>Total</td>
              <td style={detailStyles.footR}>{bd.reduce((s, b) => s + (b.count || 0), 0)}</td>
              {bdRefunds && <td style={detailStyles.footR}>{minusMoney(bd.reduce((s, b) => s + ((rowRefunds(b) || {}).amount || 0), 0))}</td>}
              <td style={detailStyles.footR}>{fmt(total)}</td>
            </tr></tfoot>
          </ScrollTable>
        </div>
      );
    }
    case 'end-of-day': {
      const txns = data.transactions || [];
      const bd = data.paymentBreakdown || [];
      return (
        <div style={detailStyles.section}>
          {bd.length > 0 && (
            <div style={{ marginBottom: txns.length ? '16px' : '0' }}>
              <p style={detailStyles.sectionLabel}>Payment Summary</p>
              <div style={{ borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: txns.length ? '16px' : '20px' }}>
                {bd.map((b, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', borderBottom: i < bd.length - 1 ? '1px solid #f3f4f6' : 'none', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <span style={{ fontSize: '13px', color: '#4b5563' }}>{b.paymentName || b.paymentType || b._id || 'Unknown'}</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', fontVariantNumeric: 'tabular-nums' }}>{fmt(b.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {txns.length > 0 && (
            <>
              <p style={detailStyles.sectionLabel}>Transactions</p>
              <ScrollTable>
                <thead><tr>
                  <th style={detailStyles.th}>Client</th>
                  <th style={detailStyles.th}>Time</th>
                  <th style={detailStyles.thR}>Amount</th>
                </tr></thead>
                <tbody>
                  {txns.slice(0, 50).map((t, i) => (
                    <tr key={i}>
                      <td style={detailStyles.tdBold}>{t.client || t.clientName || 'Walk-in'}</td>
                      <td style={detailStyles.td}>{t.time || t.date || '-'}</td>
                      <td style={detailStyles.tdR}>{fmt(t.amount || t.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </ScrollTable>
            </>
          )}
        </div>
      );
    }
    default:
      return null;
  }
};

// ── Client-side export helpers ────────────────────────────────────

/**
 * Convert report data to CSV rows based on type, trigger download
 */
const exportCSV = (reportType, data, reportName, dateRange, opts = {}) => {
  const rows = [];
  const add = (...cols) => rows.push(cols.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','));

  switch (reportType) {
    case 'holiday-report': {
      const leaveTypeLabel = (t) => t === 'sick' ? 'Sick' : t === 'other' ? 'Other' : 'Holiday';
      add('Staff', 'Start Date', 'End Date', 'Type', 'Reason', 'Days');
      (data.holidays?.staff || []).forEach(s => s.holidays.forEach(h => add(s.staffName, h.startDate, h.endDate, leaveTypeLabel(h.leaveType), h.title || '', h.days)));
      break;
    }
    case 'seat-utilisation': {
      add('Staff', 'Booked Hours', 'Available Hours', 'Bookings', 'Utilisation %');
      (data.utilisation?.staff || []).forEach(s => add(s.staffName, Math.round(s.bookedMinutes / 60), Math.round(s.availableMinutes / 60), s.bookingCount, s.rate));
      break;
    }
    case 'sales-by-staff': {
      const withRf = (data.salesByStaff || []).some((st) => rowRefunds(st));
      add('Staff', 'Services', 'Products', 'Vouchers', 'Tips', ...(withRf ? ['Refunds'] : []), 'Total', 'Bookings');
      (data.salesByStaff || []).forEach(s => add(s.name, s.serviceRevenue, s.productRevenue, s.voucherRevenue, s.tips, ...(withRf ? [-((rowRefunds(s) || {}).amount || 0)] : []), s.totalRevenue, s.bookingCount));
      break;
    }
    case 'staff-earnings': {
      const typeLabel = (r) => r.type === 'owner' ? 'Owner' : r.type === 'employed' ? 'Employed' : r.type === 'online-store' ? 'Online store' : r.type === 'unassigned' ? 'Unassigned' : 'Self-employed';
      const seRf = [...(data.business || []), ...(data.selfEmployed || [])].some((r) => Number(r.refunds) > 0);
      const rfCol = (r) => (seRf ? [-(Number(r.refunds) || 0)] : []);
      add('Section', 'Staff', 'Type', 'Service rate %', 'Product rate %', 'Services', 'Products', 'Vouchers', 'No-show / cancellation', 'Gross', 'Tax in gross', 'Card fees', 'To business', 'To stylist', 'Via Beu', 'Owed in cash', 'Paid to salon', 'Tips', 'Loyalty comps', 'Appointments', ...(seRf ? ['Refunds'] : []));
      (data.business || []).forEach(r => add('Business', r.name + (r.active === false ? ' (left)' : ''), typeLabel(r), '', '', r.services, r.products, r.vouchers, r.noShowCancelFees, r.gross, r.tax || 0, r.fees, r.toBusiness, r.toStylist, '', '', '', r.tips, r.loyaltyComps, r.bookings, ...rfCol(r)));
      (data.selfEmployed || []).forEach(r => add('Self-employed', r.name + (r.active === false ? ' (left)' : ''), typeLabel(r), (r.serviceOverrideCount > 0 ? serviceRateLabel(r) : r.serviceRate), r.productRate, r.services, r.products, r.vouchers, r.noShowCancelFees, r.gross, r.tax || 0, r.fees, r.toBusiness, r.toStylist, r.settledViaBeu, r.owedInCash, r.paidToSalon, r.tips, r.loyaltyComps, r.bookings, ...rfCol(r)));
      const t = data.totals || {};
      add();
      add('Totals', 'Salon turnover', t.salonTurnover);
      add('Totals', 'Self-employed pass-through', t.sePassThrough);
      add('Totals', 'Gross takings', t.grossTakings);
      add('Totals', 'Card fees', t.fees, data.feePaidBy === 'salon' ? 'borne by salon' : 'borne by self-employed on own bookings');
      add('Totals', 'Tips', t.tips);
      add('Totals', 'Loyalty comps', t.loyaltyComps);
      add('Totals', 'Owed in cash by self-employed', t.selfEmployed?.owedInCash);
      add('Basis', 'Appointment date');
      break;
    }
    case 'commission-payroll': {
      add('Staff', 'Service Commission', 'Product Commission', 'Voucher Commission', 'Owed', 'Paid', 'Outstanding', 'Bookings');
      (data.commission || []).forEach(c => add(c.name, c.serviceCommission, c.productCommission, c.voucherCommission, c.owed, c.paid, c.outstanding, c.bookingCount));
      break;
    }
    case 'manual-discounts': {
      add('Discount code', 'Who pays', 'Uses', 'Taken off');
      (((data.summary && data.summary.codes) || {}).byCode || []).forEach(c => add(c.code, c.fundingMode, c.count, c.total));
      add('');
      add('Date', 'Rang up by', 'Had Discounts', 'Stylist', 'Item', 'Qty', 'Menu price', 'Price before', 'Charged', 'Taken off');
      ((data.manual && data.manual.sales) || []).forEach(x => (x.lines || []).forEach(l => add(
        new Date(x.date).toISOString().slice(0, 10), x.byName || '', x.hadRight ? 'Yes' : (x.overrode ? 'No (card already taken)' : 'No'),
        x.stylistName || '', l.name || l.kind, l.qty || 1, l.menuPrice, manualFrom(l), l.charged, l.off)));
      break;
    }
    case 'tips-report': {
      add('Staff', 'Total Tips', 'Bookings', 'Average Tip');
      (data.tips || []).forEach(t => add(t.name, t.totalTips, t.tipCount, t.averageTip || 0));
      break;
    }
    case 'rebook-rate': {
      const r = data.rebooking || {};
      add('Rebook Rate', `${r.value || 0}%`);
      add('Rebooked', r.rebooked || 0);
      add('Total Appointments', r.total || 0);
      if ((data.byStaff || []).length) {
        add('');
        add('Staff', 'Rate', 'Rebooked', 'Total');
        data.byStaff.forEach(s => add(s.name, `${s.rate}%`, s.rebooked, s.total));
      }
      break;
    }
    case 'client-retention': {
      const o = data.overall || {};
      add('Retention Window', `${opts.retentionThreshold || 90} days`);
      add('Retention Rate', `${o.rate || 0}%`);
      add('Total Clients', o.totalClients || 0);
      add('Retained', o.retainedClients || 0);
      add('Lost', o.lostClients || 0);
      add('Avg Spend', o.avgSpend || 0);
      if ((data.byStaff || []).length) {
        add('');
        add('Staff', 'Rate', 'Retained', 'Clients');
        data.byStaff.forEach(s => add(s.name, `${s.rate}%`, s.retainedClients, s.totalClients));
      }
      if ((data.clients || []).length) {
        add('');
        add('Client', 'Status', 'Visits', 'Spent', 'Last Visit');
        data.clients.forEach(c => add(c.name, c.retained ? 'Retained' : 'Lost', c.visits, c.totalSpent, c.lastVisit));
      }
      break;
    }
    case 'new-vs-returning': {
      const r = data.retention || {};
      add('New Clients', r.newClients || 0);
      add('Returning Clients', r.returningClients || 0);
      add('Return Rate', `${r.returningClientPercentage || 0}%`);
      add('Avg Spend per Client', r.avgRevenuePerClient || 0);
      add('Avg Visits per Client', r.avgBookingsPerClient || 0);
      if ((data.clients || []).length) {
        add('');
        add('Client', 'Type', 'Visits', 'Total Spent', 'Avg Spend', 'Last Visit');
        data.clients.forEach(c => add(c.name, c.isNew ? 'New' : 'Returning', c.visits, c.totalSpent, c.avgSpend, c.lastVisit));
      }
      if ((data.newClients || []).length) {
        add('');
        add('New Client', 'Source', 'Staff', 'Date', 'Spent');
        data.newClients.forEach(c => add(c.name || 'Unknown', c.source || 'Direct', c.staff || '-', c.date || '-', c.totalSpent || 0));
      }
      break;
    }
    case 'payment-methods': {
      const withRf = (data.breakdown || []).some((b) => rowRefunds(b));
      add('Payment Method', 'Transactions', ...(withRf ? ['Refunded'] : []), 'Total');
      (data.breakdown || []).forEach(b => add(b.paymentName || b.paymentType || b._id || 'Unknown', b.count, ...(withRf ? [-((rowRefunds(b) || {}).amount || 0)] : []), b.total));
      break;
    }
    case 'product-sales': {
      if ((data.byStaff || []).length) {
        add('Staff', 'Items Sold', 'Revenue');
        data.byStaff.forEach(s => add(s.name, s.totalQuantity, s.totalRevenue));
        add('');
      }
      add('Product', 'Quantity', 'Revenue', 'Avg Price');
      (data.products || []).forEach(p => add(p.productName || p.name, p.totalQuantity, p.totalRevenue, p.avgPrice));
      break;
    }
    case 'inventory-report': {
      add('Product', 'Category', 'Quantity', 'Cost Value', 'Retail Value');
      (data.inventory?.items || []).forEach(p => add(p.name, p.category, p.quantity, p.stockValue, p.retailValue));
      break;
    }
    case 'gift-vouchers': {
      const v = data.vouchers || {};
      if ((data.byStaff || []).length) {
        add('Staff', 'Voucher Sales', 'Revenue');
        data.byStaff.forEach(s => add(s.name, s.count, s.totalRevenue));
        add('');
      }
      add('Voucher Name', 'Qty Sold', 'Total Sold');
      (v.sold?.breakdown || []).forEach(s => add(s.name, s.count, s.total));
      add('Total', v.sold?.count || 0, v.sold?.total || 0);
      add('');
      add('Redemption Type', 'Count', 'Total');
      add('Full Redemption', v.redeemed?.fullRedemptions || 0, '');
      add('Partial Redemption', v.redeemed?.partialRedemptions || 0, '');
      add('Total Redeemed', v.redeemed?.count || 0, v.redeemed?.total || 0);
      add('');
      add('Outstanding Liability', '', Math.round(((v.sold?.total || 0) - (v.redeemed?.total || 0)) * 100) / 100);
      break;
    }
    case 'loyalty': {
      const l = data.loyalty || {};
      const redeemed = l.redeemed || {};
      const funding = redeemed.funding || {};
      add('Rewards Redeemed', redeemed.count || 0, redeemed.total || 0);
      add('');
      add('Funded By', 'Rewards', 'Value');
      add('Salon-funded', funding.salon?.count || 0, funding.salon?.total || 0);
      add('Stylist-funded', funding.stylist?.count || 0, funding.stylist?.total || 0);
      if ((redeemed.breakdown || []).length) {
        add('');
        add('Scheme', 'Redeemed', 'Value');
        redeemed.breakdown.forEach(s => add(s.name || 'Scheme', s.count || 0, s.total || 0));
      }
      if ((l.byStaff || []).length) {
        add('');
        add('Staff', 'Rewards', 'Salon-funded', 'Stylist-funded', 'Value');
        l.byStaff.forEach(s => add(s.name || 'Unknown', s.count || 0, s.salonFundedCount || 0, s.stylistFundedCount || 0, s.total || 0));
      }
      add('');
      add('Banked Rewards Owed', l.liability?.bankedRewards || 0);
      add('Clients With Reward Ready', l.liability?.clientsWithRewards || 0);
      add('Clients In Progress', l.liability?.clientsInProgress || 0);
      break;
    }
    case 'revenue-summary': {
      const withRf = (data.paymentBreakdown || []).some((b) => rowRefunds(b));
      add('Payment Method', 'Transactions', ...(withRf ? ['Refunded'] : []), 'Total');
      (data.paymentBreakdown || []).forEach(b => add(b.paymentName || b.paymentType || b._id || 'Unknown', b.count, ...(withRf ? [-((rowRefunds(b) || {}).amount || 0)] : []), b.total));
      break;
    }
    case 'vat-report': {
      const t = data.totals || {};
      const rf = data.refunded || {};
      const refd = (r) => -((r.refunded && r.refunded.tax) || 0);
      const due = (r) => (r.due != null ? r.due : r.tax);
      add('Section', 'Name', 'Rate %', 'Net', 'Tax', 'Refunded tax', 'Tax due', 'Gross', 'Sales / lines');
      add('Totals', 'All', '', t.net, t.tax, -(rf.tax || 0), data.due != null ? data.due : t.tax, t.gross, t.sales);
      (data.bySite || []).forEach(r => add('By location', r.name, '', r.net, r.tax, refd(r), due(r), r.gross, r.sales));
      (data.byRate || []).forEach(r => add('By rate', r.name, r.rate, r.net, r.tax, refd(r), due(r), r.gross, r.lines));
      (data.bySeller || []).forEach(r => add('By seller', r.name + (r.taxNumber ? ` (${r.taxNumber})` : ''), '', r.net, r.tax, refd(r), due(r), r.gross, r.sales));
      const domainLabel = (d) => d === 'services' ? 'Services' : d === 'products' ? 'Products' : d === 'vouchers' ? 'Vouchers' : d === 'shipping' ? 'Delivery' : d;
      (data.byDomain || []).forEach(r => add('By type', domainLabel(r.domain), '', r.net, r.tax, refd(r), due(r), r.gross, ''));
      add('Basis', `Tax frozen at the time of each sale; ${rf.refunds || 0} Stripe refund(s) made in the period deducted`);
      break;
    }
    case 'end-of-day': {
      if ((data.paymentBreakdown || []).length) {
        add('Payment Method', 'Total');
        data.paymentBreakdown.forEach(b => add(b.paymentName || b.paymentType || b._id || 'Unknown', b.total));
        add('');
      }
      add('Client', 'Time', 'Amount');
      (data.transactions || []).forEach(t => add(t.client || t.clientName || 'Walk-in', t.time || t.date || '-', t.amount || t.totalAmount));
      break;
    }
    default: break;
  }

  // Refunds made in the period (docs/refunds.md), under any report that has them.
  const rs = reportRefundSummary(reportType, data);
  if (rs) {
    add('');
    add('Refunds', rs.count != null ? `${rs.count}` : '', -rs.amount);
    if (rs.net != null) add('Net takings', '', rs.net);
  }

  if (!rows.length) return;
  // BOM so Excel opens £ and accents correctly; saveFile picks download / share sheet / native per platform.
  const blob = new Blob(['\ufeff' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  return saveFile({ blob, filename: `${reportName.replace(/\s+/g, '_')}_${dateRange.replace(/\s+/g, '_')}.csv`, title: reportName });
};

/**
 * Generate a branded HTML report and open print dialog (Save as PDF)
 */
const exportPDF = async (reportType, data, reportName, dateRange, metrics, opts = {}) => {
  const fmtD = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  // Every table the HTML below is built from is also kept here, so the SAME
  // data can be rendered to a real PDF file where a print dialog can't be
  // relied on (Capacitor app, phones, popup blocked).
  const pdfTables = [];
  const fmtN = (v) => v != null ? `£${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '£0.00';

  const buildTable = (headers, rows, footRow) => {
    const ths = headers.map(h => `<th style="text-align:${h.align || 'left'};padding:8px 12px;font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;border-bottom:2px solid #e5e7eb;background:#f9fafb">${h.label}</th>`).join('');
    const trs = rows.map((row, ri) => {
      const tds = row.map((cell, ci) => `<td style="padding:9px 12px;font-size:12px;color:#1f2937;border-bottom:1px solid #f3f4f6;${headers[ci].align === 'right' ? 'text-align:right;font-variant-numeric:tabular-nums;font-weight:500' : ''}${cell.bold ? ';font-weight:600' : ''}">${cell.v}</td>`).join('');
      return `<tr${ri % 2 === 1 ? ' style="background:#fafafa"' : ''}>${tds}</tr>`;
    }).join('');
    pdfTables.push({ headers, rows, foot: footRow });
    const foot = footRow ? `<tfoot><tr>${footRow.map((c, ci) => `<td style="padding:9px 12px;font-size:12px;font-weight:600;color:#1f2937;border-top:2px solid #e5e7eb;background:#f9fafb;${headers[ci].align === 'right' ? 'text-align:right;font-variant-numeric:tabular-nums' : ''}">${c}</td>`).join('')}</tr></tfoot>` : '';
    return `<table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;margin-bottom:16px"><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody>${foot}</table>`;
  };

  let body = '';
  switch (reportType) {
    case 'holiday-report': {
      const staff = data.holidays?.staff || [];
      const leaveTypeLabel = (t) => t === 'sick' ? 'Sick' : t === 'other' ? 'Other' : 'Holiday';
      const rows = staff.flatMap(s => s.holidays.map((h, i) => [
        { v: i === 0 ? s.staffName : '', bold: i === 0 },
        { v: `${fmtD(h.startDate)} – ${fmtD(h.endDate)}` },
        { v: leaveTypeLabel(h.leaveType) },
        { v: h.title || '' },
        { v: h.days },
      ]));
      const total = staff.reduce((s, st) => s + st.holidays.reduce((a, h) => a + (h.days || 0), 0), 0);
      body = buildTable(
        [{ label: 'Staff' }, { label: 'Period' }, { label: 'Type' }, { label: 'Reason' }, { label: 'Days', align: 'right' }],
        rows,
        ['Total', '', '', '', total]
      );
      break;
    }
    case 'seat-utilisation': {
      const staff = data.utilisation?.staff || [];
      const rows = staff.map(s => [{ v: s.staffName, bold: true }, { v: `${s.rate}%` }, { v: `${Math.round(s.bookedMinutes / 60)}h` }, { v: `${Math.round(s.availableMinutes / 60)}h` }, { v: s.bookingCount }]);
      body = buildTable([{ label: 'Staff' }, { label: 'Rate', align: 'right' }, { label: 'Booked', align: 'right' }, { label: 'Available', align: 'right' }, { label: 'Appts', align: 'right' }], rows);
      break;
    }
    case 'staff-earnings': {
      const typeLabel = (r) => r.type === 'owner' ? 'Owner' : r.type === 'employed' ? 'Employed' : r.type === 'online-store' ? 'Online store' : r.type === 'unassigned' ? 'Unassigned' : 'Self-employed';
      const business = data.business || [];
      const se = data.selfEmployed || [];
      const t = data.totals || {};
      const bt = t.business || {};
      const st = t.selfEmployed || {};
      const pdfTax = (tot, rows) => (tot.tax != null ? Number(tot.tax) || 0
        : Math.round(rows.reduce((s, r) => s + (Number(r.tax) || 0), 0) * 100) / 100);
      const bRows = business.map(r => [{ v: r.name + (r.active === false ? ' (left)' : ''), bold: true }, { v: typeLabel(r) }, { v: fmtN(r.services) }, { v: fmtN(r.products) }, { v: fmtN(r.vouchers) }, { v: fmtN(r.noShowCancelFees) }, { v: fmtN(r.gross) }, { v: fmtN(r.tax || 0) }, { v: fmtN(r.fees) }, { v: fmtN(r.toBusiness), bold: true }, { v: fmtN(r.tips) }]);
      body += '<p style="font-size:12px;font-weight:600;color:#6b7280;margin:0 0 6px 0">Business — owner, employed staff, online store</p>';
      body += buildTable(
        [{ label: 'Staff' }, { label: 'Type' }, { label: 'Services', align: 'right' }, { label: 'Products', align: 'right' }, { label: 'Vouchers', align: 'right' }, { label: 'No-show / cancel', align: 'right' }, { label: 'Gross', align: 'right' }, { label: 'Tax', align: 'right' }, { label: 'Card fees', align: 'right' }, { label: 'To business', align: 'right' }, { label: 'Tips', align: 'right' }],
        bRows,
        ['Total', '', fmtN(bt.services), fmtN(bt.products), fmtN(bt.vouchers), fmtN(bt.noShowCancelFees), fmtN(bt.gross), fmtN(pdfTax(bt, business)), fmtN(bt.fees), fmtN(bt.toBusiness), fmtN(bt.tips)]
      );
      const sRows = se.map(r => [{ v: r.name + (r.active === false ? ' (left)' : ''), bold: true }, { v: serviceRateLabel(r) }, { v: fmtN(r.services) }, { v: fmtN(r.products) }, { v: fmtN(r.vouchers) }, { v: fmtN(r.noShowCancelFees) }, { v: fmtN(r.gross) }, { v: fmtN(r.tax || 0) }, { v: fmtN(r.fees) }, { v: fmtN(r.toBusiness), bold: true }, { v: fmtN(r.toStylist), bold: true }, { v: fmtN(r.settledViaBeu) }, { v: fmtN(r.owedInCash) }, { v: fmtN(r.paidToSalon) }, { v: fmtN(r.tips) }]);
      body += '<p style="font-size:12px;font-weight:600;color:#6b7280;margin:0 0 6px 0">Self-employed — gross is pass-through; only "To business" is salon turnover</p>';
      body += buildTable(
        [{ label: 'Staff' }, { label: 'Rate', align: 'right' }, { label: 'Services', align: 'right' }, { label: 'Products', align: 'right' }, { label: 'Vouchers', align: 'right' }, { label: 'No-show / cancel', align: 'right' }, { label: 'Gross', align: 'right' }, { label: 'Tax', align: 'right' }, { label: 'Card fees', align: 'right' }, { label: 'To business', align: 'right' }, { label: 'To stylist', align: 'right' }, { label: 'Via Beu', align: 'right' }, { label: 'Owed in cash', align: 'right' }, { label: 'Paid to salon', align: 'right' }, { label: 'Tips', align: 'right' }],
        sRows,
        ['Total', '', fmtN(st.services), fmtN(st.products), fmtN(st.vouchers), fmtN(st.noShowCancelFees), fmtN(st.gross), fmtN(pdfTax(st, se)), fmtN(st.fees), fmtN(st.toBusiness), fmtN(st.toStylist), fmtN(st.settledViaBeu), fmtN(st.owedInCash), fmtN(st.paidToSalon), fmtN(st.tips)]
      );
      body += buildTable(
        [{ label: 'Totals for the period' }, { label: '', align: 'right' }],
        [
          [{ v: 'Salon turnover', bold: true }, { v: fmtN(t.salonTurnover), bold: true }],
          [{ v: 'Self-employed pass-through', bold: true }, { v: fmtN(t.sePassThrough), bold: true }],
          [{ v: 'Gross takings' }, { v: fmtN(t.grossTakings) }],
          [{ v: `Card fees (${data.feePaidBy === 'salon' ? 'borne by salon' : 'borne by self-employed on own bookings'})` }, { v: fmtN(t.fees) }],
          [{ v: 'Tips (to stylists, outside the split)' }, { v: fmtN(t.tips) }],
          [{ v: 'Owed in cash by self-employed (= Commission Tracker, same dates)' }, { v: fmtN(st.owedInCash) }]
        ]
      );
      body += '<p style="font-size:11px;color:#9ca3af;margin:0 0 12px 0">Period by appointment date, matching Overview. Card fees on no-show and cancellation charges are not attributed.</p>';
      break;
    }
    case 'sales-by-staff': {
      const staff = data.salesByStaff || [];
      const rows = staff.map(s => [{ v: s.name, bold: true }, { v: fmtN(s.serviceRevenue) }, { v: fmtN(s.productRevenue) }, { v: fmtN(s.voucherRevenue) }, { v: fmtN(s.tips) }, { v: fmtN(s.totalRevenue) }, { v: s.bookingCount }]);
      const foot = ['Total', fmtN(staff.reduce((s, st) => s + (st.serviceRevenue || 0), 0)), fmtN(staff.reduce((s, st) => s + (st.productRevenue || 0), 0)), fmtN(staff.reduce((s, st) => s + (st.voucherRevenue || 0), 0)), fmtN(staff.reduce((s, st) => s + (st.tips || 0), 0)), fmtN(staff.reduce((s, st) => s + (st.totalRevenue || 0), 0)), staff.reduce((s, st) => s + (st.bookingCount || 0), 0)];
      body = buildTable([{ label: 'Staff' }, { label: 'Services', align: 'right' }, { label: 'Products', align: 'right' }, { label: 'Vouchers', align: 'right' }, { label: 'Tips', align: 'right' }, { label: 'Total', align: 'right' }, { label: 'Appts', align: 'right' }], rows, foot);
      break;
    }
    case 'commission-payroll': {
      const comm = data.commission || [];
      const rows = comm.map(c => [{ v: c.name, bold: true }, { v: fmtN(c.serviceCommission) }, { v: fmtN(c.productCommission) }, { v: fmtN(c.totalCommission) }]);
      const foot = ['Total', fmtN(comm.reduce((s, c) => s + (c.serviceCommission || 0), 0)), fmtN(comm.reduce((s, c) => s + (c.productCommission || 0), 0)), fmtN(comm.reduce((s, c) => s + (c.totalCommission || 0), 0))];
      body = buildTable([{ label: 'Staff' }, { label: 'Service', align: 'right' }, { label: 'Product', align: 'right' }, { label: 'Total', align: 'right' }], rows, foot);
      break;
    }
    case 'vat-report': {
      const t = data.totals || {};
      const sellers = data.bySeller || [];
      const domainLabel = (d) => d === 'services' ? 'Services' : d === 'products' ? 'Products' : d === 'vouchers' ? 'Vouchers' : d === 'shipping' ? 'Delivery' : d;
      // Header line the accountant wants first: whose return this is.
      body += `<p style="font-size:12px;color:#6b7280;margin:0 0 10px 0">${sellers.map(s => `${s.name}${s.taxNumber ? ` — Tax no. ${s.taxNumber}` : ''}`).join(' · ') || 'No taxed sales in this period'}</p>`;
      const rf = data.refunded || {};
      const hasRef = rf.refunds > 0;
      body += `<p style="font-size:13px;margin:0 0 12px 0"><strong>Tax collected ${fmtN(t.tax)}</strong> · Net ${fmtN(t.net)} · Gross ${fmtN(t.gross)} · ${t.sales || 0} sales</p>`;
      if (hasRef) body += `<p style="font-size:13px;margin:0 0 12px 0">Refunded ${fmtN(-(rf.tax || 0))} (${rf.refunds} refund${rf.refunds === 1 ? '' : 's'}) · <strong>Tax due ${fmtN(data.due)}</strong></p>`;
      const refCols = hasRef ? [{ label: 'Refunded', align: 'right' }, { label: 'Due', align: 'right' }] : [];
      const refCells = (r) => hasRef ? [{ v: fmtN(-((r.refunded && r.refunded.tax) || 0)) }, { v: fmtN(r.due != null ? r.due : r.tax) }] : [];
      if (Array.isArray(data.bySite) && data.bySite.length) {
        body += '<p style="font-size:12px;font-weight:600;color:#6b7280;margin:0 0 6px 0">By location</p>';
        body += buildTable([{ label: 'Location' }, { label: 'Net', align: 'right' }, { label: 'Tax', align: 'right' }, ...refCols, { label: 'Gross', align: 'right' }, { label: 'Sales', align: 'right' }],
          data.bySite.map(r => [{ v: r.name, bold: true }, { v: fmtN(r.net) }, { v: fmtN(r.tax) }, ...refCells(r), { v: fmtN(r.gross) }, { v: r.sales }]));
        body += '<p style="font-size:12px;font-weight:600;color:#6b7280;margin:12px 0 6px 0">By rate</p>';
      } else body += '<p style="font-size:12px;font-weight:600;color:#6b7280;margin:0 0 6px 0">By rate</p>';
      body += buildTable([{ label: 'Rate' }, { label: 'Net', align: 'right' }, { label: 'Tax', align: 'right' }, ...refCols, { label: 'Gross', align: 'right' }, { label: 'Lines', align: 'right' }],
        (data.byRate || []).map(r => [{ v: `${r.name} ${r.rate}%`, bold: true }, { v: fmtN(r.net) }, { v: fmtN(r.tax) }, ...refCells(r), { v: fmtN(r.gross) }, { v: r.lines }]),
        ['Total', fmtN(t.net), fmtN(t.tax), ...(hasRef ? [fmtN(-(rf.tax || 0)), fmtN(data.due)] : []), fmtN(t.gross), `${t.sales || 0} sales`]);
      body += '<p style="font-size:12px;font-weight:600;color:#6b7280;margin:12px 0 6px 0">By seller</p>';
      body += buildTable([{ label: 'Seller' }, { label: 'Tax no.' }, { label: 'Net', align: 'right' }, { label: 'Tax', align: 'right' }, ...refCols, { label: 'Gross', align: 'right' }, { label: 'Sales', align: 'right' }],
        sellers.map(s => [{ v: s.name, bold: true }, { v: s.taxNumber || '—' }, { v: fmtN(s.net) }, { v: fmtN(s.tax) }, ...refCells(s), { v: fmtN(s.gross) }, { v: s.sales }]));
      body += '<p style="font-size:12px;font-weight:600;color:#6b7280;margin:12px 0 6px 0">By type</p>';
      body += buildTable([{ label: 'Type' }, { label: 'Net', align: 'right' }, { label: 'Tax', align: 'right' }, ...refCols, { label: 'Gross', align: 'right' }],
        (data.byDomain || []).map(d => [{ v: domainLabel(d.domain), bold: true }, { v: fmtN(d.net) }, { v: fmtN(d.tax) }, ...refCells(d), { v: fmtN(d.gross) }]));
      body += `<p style="font-size:11px;color:#9ca3af;margin:12px 0 0 0">Tax is frozen at the time of each sale. ${hasRef ? 'Refunds made in this period are deducted; they count in the period they were made, not the period of the sale.' : 'No refunds were made in this period.'}</p>`;
      break;
    }
    case 'manual-discounts': {
      const m = data.manual || {};
      const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
      const cs = (data.summary && data.summary.codes) || {};
      if ((cs.byCode || []).length) {
        body += '<p style="font-size:12px;font-weight:600;color:#6b7280;margin:0 0 6px 0">Discount codes</p>';
        body += buildTable([{ label: 'Code' }, { label: 'Who pays' }, { label: 'Uses', align: 'right' }, { label: 'Taken off', align: 'right' }],
          cs.byCode.map(c => [{ v: esc(c.code), bold: true }, { v: ({ salon: 'Salon pays', stylist: 'Stylist pays', shared: 'Shared' })[c.fundingMode] || '' }, { v: c.count }, { v: fmtN(c.total) }]),
          ['Total', `Salon ${fmtN(cs.salonAbsorbed || 0)} · stylists ${fmtN(cs.stylistAbsorbed || 0)}`, cs.count || 0, fmtN(cs.total || 0)]);
        body += '<p style="font-size:12px;font-weight:600;color:#6b7280;margin:12px 0 6px 0">Taken off by hand</p>';
      }
      body += buildTable([{ label: 'Rang up by' }, { label: 'Taken off', align: 'right' }, { label: 'Sales', align: 'right' }, { label: 'Without Discounts', align: 'right' }],
        (m.byStaff || []).map(k => [{ v: esc(k.name), bold: true }, { v: fmtN(k.amount) }, { v: k.sales }, { v: k.withoutRight || 0 }]),
        ['Total', fmtN((m.totals || {}).amount || 0), (m.totals || {}).sales || 0, (m.totals || {}).withoutRight || 0]);
      body += '<p style="font-size:12px;font-weight:600;color:#6b7280;margin:12px 0 6px 0">Each sale</p>';
      body += buildTable([{ label: 'Date' }, { label: 'Rang up by' }, { label: 'What changed' }, { label: 'Taken off', align: 'right' }],
        (m.sales || []).map(x => [
          { v: new Date(x.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) },
          { v: esc(x.byName || 'Unknown') + (x.hadRight ? '' : ' (no Discounts)'), bold: true },
          { v: (x.lines || []).map(l => `${esc(l.name || l.kind)}${l.qty > 1 ? ` × ${l.qty}` : ''}: ${fmtN(manualFrom(l))} → ${fmtN(l.charged)}`).join('<br/>') },
          { v: fmtN(x.amount) }]));
      break;
    }
    case 'tips-report': {
      const tips = data.tips || [];
      const rows = tips.map(t => [{ v: t.name, bold: true }, { v: fmtN(t.totalTips) }, { v: t.tipCount }, { v: fmtN(t.averageTip || 0) }]);
      body = buildTable([{ label: 'Staff' }, { label: 'Total Tips', align: 'right' }, { label: 'Bookings', align: 'right' }, { label: 'Avg Tip', align: 'right' }], rows);
      break;
    }
    case 'payment-methods':
    case 'revenue-summary': {
      const bd = data.breakdown || data.paymentBreakdown || [];
      const withRf = bd.some((b) => rowRefunds(b));
      const rfCell = (b) => (withRf ? [{ v: rowRefunds(b) ? minusMoney(rowRefunds(b).amount) : '£0.00' }] : []);
      const rows = bd.map(b => [{ v: b.paymentName || b.paymentType || b._id || 'Unknown', bold: true }, { v: b.count }, ...rfCell(b), { v: fmtN(b.total) }]);
      const foot = ['Total', bd.reduce((s, b) => s + (b.count || 0), 0), ...(withRf ? [minusMoney(bd.reduce((s, b) => s + ((rowRefunds(b) || {}).amount || 0), 0))] : []), fmtN(bd.reduce((s, b) => s + (b.total || 0), 0))];
      body = buildTable([{ label: 'Method' }, { label: 'Transactions', align: 'right' }, ...(withRf ? [{ label: 'Refunded', align: 'right' }] : []), { label: 'Total', align: 'right' }], rows, foot);
      break;
    }
    case 'product-sales': {
      const pByStaff = data.byStaff || [];
      if (pByStaff.length) {
        const sRows = pByStaff.map(s => [{ v: s.name, bold: true }, { v: s.totalQuantity }, { v: fmtN(s.totalRevenue) }]);
        const sFoot = ['Total', pByStaff.reduce((s, st) => s + (st.totalQuantity || 0), 0), fmtN(pByStaff.reduce((s, st) => s + (st.totalRevenue || 0), 0))];
        body = '<div style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:8px">By Staff</div>' + buildTable([{ label: 'Staff' }, { label: 'Items', align: 'right' }, { label: 'Revenue', align: 'right' }], sRows, sFoot);
      }
      const products = data.products || [];
      const rows = products.map(p => [{ v: p.productName || p.name, bold: true }, { v: p.totalQuantity }, { v: fmtN(p.totalRevenue) }, { v: fmtN(p.avgPrice) }]);
      body += buildTable([{ label: 'Product' }, { label: 'Qty', align: 'right' }, { label: 'Revenue', align: 'right' }, { label: 'Avg Price', align: 'right' }], rows);
      break;
    }
    case 'inventory-report': {
      const items = data.inventory?.items || [];
      const rows = items.map(p => [{ v: p.name, bold: true }, { v: p.category }, { v: p.quantity }, { v: fmtN(p.stockValue) }, { v: fmtN(p.retailValue) }]);
      body = buildTable([{ label: 'Product' }, { label: 'Category' }, { label: 'Qty', align: 'right' }, { label: 'Cost', align: 'right' }, { label: 'Retail', align: 'right' }], rows);
      break;
    }
    case 'rebook-rate': {
      const r = data.rebooking || {};
      const byStaff = data.byStaff || [];
      body = `<div style="padding:16px;background:#f9fafb;border-radius:8px;margin-bottom:16px;border:1px solid #e5e7eb;text-align:center"><div style="font-size:32px;font-weight:700;color:${(r.value||0) >= 60 ? '#16a34a' : (r.value||0) >= 40 ? '#f59e0b' : '#dc2626'}">${r.value || 0}%</div><div style="font-size:12px;color:#6b7280;margin-top:4px">${r.rebooked || 0} rebooked of ${r.total || 0} appointments</div></div>`;
      if (byStaff.length) {
        const rows = byStaff.map(s => [{ v: s.name, bold: true }, { v: `${s.rate}%` }, { v: s.rebooked }, { v: s.total }]);
        body += buildTable([{ label: 'Staff' }, { label: 'Rate', align: 'right' }, { label: 'Rebooked', align: 'right' }, { label: 'Total', align: 'right' }], rows);
      }
      break;
    }
    case 'client-retention': {
      const o = data.overall || {};
      const byStaff = data.byStaff || [];
      const clients = data.clients || [];
      body = `<div style="padding:16px;background:#f9fafb;border-radius:8px;margin-bottom:16px;border:1px solid #e5e7eb;text-align:center"><div style="font-size:32px;font-weight:700;color:${(o.rate||0) >= 60 ? '#16a34a' : (o.rate||0) >= 40 ? '#f59e0b' : '#dc2626'}">${o.rate || 0}%</div><div style="font-size:12px;color:#6b7280;margin-top:4px">${o.retainedClients || 0} retained of ${o.totalClients || 0} clients</div><div style="font-size:11px;color:#9ca3af;margin-top:2px">Booked again within ${opts.retentionThreshold || 90} days</div></div>`;
      if (byStaff.length) {
        const rows = byStaff.map(s => [{ v: s.name, bold: true }, { v: `${s.rate}%` }, { v: s.retainedClients }, { v: s.totalClients }]);
        body += buildTable([{ label: 'Staff' }, { label: 'Rate', align: 'right' }, { label: 'Retained', align: 'right' }, { label: 'Clients', align: 'right' }], rows);
      }
      if (clients.length) {
        const rows = clients.map(c => [{ v: c.name, bold: true }, { v: c.retained ? 'Retained' : 'Lost' }, { v: c.visits }, { v: fmtN(c.totalSpent) }]);
        body += buildTable([{ label: 'Client' }, { label: 'Status' }, { label: 'Visits', align: 'right' }, { label: 'Spent', align: 'right' }], rows);
      }
      break;
    }
    case 'new-vs-returning': {
      const r = data.retention || {};
      const clients = data.clients || [];
      const nc = data.newClients || [];
      body = `<div style="display:flex;gap:12px;margin-bottom:16px"><div style="flex:1;text-align:center;padding:14px;background:#f0f9ff;border-radius:8px"><div style="font-size:24px;font-weight:700;color:#007EA0">${r.newClients || 0}</div><div style="font-size:11px;color:#6b7280">New</div></div><div style="flex:1;text-align:center;padding:14px;background:#f0fdf4;border-radius:8px"><div style="font-size:24px;font-weight:700;color:#16a34a">${r.returningClients || 0}</div><div style="font-size:11px;color:#6b7280">Returning</div></div><div style="flex:1;text-align:center;padding:14px;background:#f9fafb;border-radius:8px"><div style="font-size:24px;font-weight:700;color:#1f2937">${fmtN(r.avgRevenuePerClient)}</div><div style="font-size:11px;color:#6b7280">Avg Spend</div></div></div>`;
      if (clients.length) {
        const rows = clients.map(c => [{ v: c.name, bold: true }, { v: c.isNew ? 'New' : 'Returning' }, { v: c.visits }, { v: fmtN(c.totalSpent) }, { v: fmtN(c.avgSpend) }]);
        const foot = [`Total (${clients.length})`, '', clients.reduce((s, c) => s + (c.visits || 0), 0), fmtN(clients.reduce((s, c) => s + (c.totalSpent || 0), 0)), ''];
        body += buildTable([{ label: 'Client' }, { label: 'Type' }, { label: 'Visits', align: 'right' }, { label: 'Spent', align: 'right' }, { label: 'Avg', align: 'right' }], rows, foot);
      }
      if (nc.length) {
        body += '<div style="margin-top:12px;font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:8px">New Clients</div>';
        const rows = nc.map(c => [{ v: c.name || 'Unknown', bold: true }, { v: c.source || 'Direct' }, { v: c.staff || '-' }, { v: fmtN(c.totalSpent) }]);
        body += buildTable([{ label: 'Client' }, { label: 'Source' }, { label: 'Staff' }, { label: 'Spent', align: 'right' }], rows);
      }
      break;
    }
    case 'gift-vouchers': {
      const vByStaff = data.byStaff || [];
      if (vByStaff.length) {
        const sRows = vByStaff.map(s => [{ v: s.name, bold: true }, { v: s.count }, { v: fmtN(s.totalRevenue) }]);
        const sFoot = ['Total', vByStaff.reduce((s, st) => s + (st.count || 0), 0), fmtN(vByStaff.reduce((s, st) => s + (st.totalRevenue || 0), 0))];
        body = '<div style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:8px">By Staff</div>' + buildTable([{ label: 'Staff' }, { label: 'Sales', align: 'right' }, { label: 'Revenue', align: 'right' }], sRows, sFoot);
      }
      const vd = data.vouchers || {};
      if ((vd.sold?.breakdown || []).length) {
        const rows = vd.sold.breakdown.map(s => [{ v: s.name, bold: true }, { v: s.count }, { v: fmtN(s.total) }]);
        const foot = ['Total', vd.sold?.count || 0, fmtN(vd.sold?.total)];
        body += '<div style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin:12px 0 8px">Vouchers Sold</div>' + buildTable([{ label: 'Voucher' }, { label: 'Qty', align: 'right' }, { label: 'Total', align: 'right' }], rows, foot);
      }
      body += `<div style="display:flex;gap:12px;margin-top:12px"><div style="flex:1;text-align:center;padding:12px;background:#f0fdf4;border-radius:8px"><div style="font-size:18px;font-weight:700;color:#16a34a">${fmtN(vd.redeemed?.total)}</div><div style="font-size:11px;color:#6b7280">Redeemed (${vd.redeemed?.count || 0})</div></div><div style="flex:1;text-align:center;padding:12px;background:#fffbeb;border-radius:8px"><div style="font-size:18px;font-weight:700;color:#d97706">${fmtN(Math.round(((vd.sold?.total || 0) - (vd.redeemed?.total || 0)) * 100) / 100)}</div><div style="font-size:11px;color:#6b7280">Outstanding</div></div></div>`;
      break;
    }
    case 'loyalty': {
      const l = data.loyalty || {};
      const redeemed = l.redeemed || {};
      const funding = redeemed.funding || {};
      const salonFund = funding.salon || { count: 0, total: 0 };
      const stylistFund = funding.stylist || { count: 0, total: 0 };
      const liability = l.liability || {};
      body = `<div style="display:flex;gap:12px;margin-bottom:16px"><div style="flex:1;text-align:center;padding:14px;background:#fdf2f8;border-radius:8px"><div style="font-size:24px;font-weight:700;color:#ec4899">${fmtN(redeemed.total)}</div><div style="font-size:11px;color:#6b7280">Redeemed (${redeemed.count || 0})</div></div><div style="flex:1;text-align:center;padding:14px;background:#fffbeb;border-radius:8px"><div style="font-size:24px;font-weight:700;color:#d97706">${liability.bankedRewards || 0}</div><div style="font-size:11px;color:#6b7280">Banked Rewards Owed</div></div></div>`;
      if ((salonFund.total || 0) + (stylistFund.total || 0) > 0) {
        const rows = [
          [{ v: 'Salon-funded', bold: true }, { v: salonFund.count || 0 }, { v: fmtN(salonFund.total) }],
          [{ v: 'Stylist-funded', bold: true }, { v: stylistFund.count || 0 }, { v: fmtN(stylistFund.total) }],
        ];
        const foot = ['Total', (salonFund.count || 0) + (stylistFund.count || 0), fmtN((salonFund.total || 0) + (stylistFund.total || 0))];
        body += '<div style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin:12px 0 8px">Funded By</div>' + buildTable([{ label: 'Source' }, { label: 'Rewards', align: 'right' }, { label: 'Value', align: 'right' }], rows, foot);
      }
      if ((redeemed.breakdown || []).length) {
        const rows = redeemed.breakdown.map(s => [{ v: s.name || 'Scheme', bold: true }, { v: s.count || 0 }, { v: fmtN(s.total) }]);
        const foot = ['Total', redeemed.breakdown.reduce((a, s) => a + (s.count || 0), 0), fmtN(redeemed.breakdown.reduce((a, s) => a + (s.total || 0), 0))];
        body += '<div style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin:12px 0 8px">By Scheme</div>' + buildTable([{ label: 'Scheme' }, { label: 'Redeemed', align: 'right' }, { label: 'Value', align: 'right' }], rows, foot);
      }
      if ((l.byStaff || []).length) {
        const rows = l.byStaff.map(s => [{ v: s.name || 'Unknown', bold: true }, { v: s.count || 0 }, { v: `${s.salonFundedCount || 0} / ${s.stylistFundedCount || 0}` }, { v: fmtN(s.total) }]);
        const foot = ['Total', l.byStaff.reduce((a, s) => a + (s.count || 0), 0), `${l.byStaff.reduce((a, s) => a + (s.salonFundedCount || 0), 0)} / ${l.byStaff.reduce((a, s) => a + (s.stylistFundedCount || 0), 0)}`, fmtN(l.byStaff.reduce((a, s) => a + (s.total || 0), 0))];
        body += '<div style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin:12px 0 8px">Rewards Redeemed By Staff</div>' + buildTable([{ label: 'Staff' }, { label: 'Rewards', align: 'right' }, { label: 'Salon / Stylist', align: 'right' }, { label: 'Value', align: 'right' }], rows, foot);
      }
      break;
    }
    default: {
      body = '<p style="color:#6b7280;font-size:13px">Report data exported.</p>';
    }
  }

  // Refunds made in the period, under any report that has them.
  const rfs = reportRefundSummary(reportType, data);
  if (rfs) {
    const rfRows = [[{ v: `Refunds${rfs.count ? ` (${rfs.count})` : ''}`, bold: true }, { v: minusMoney(rfs.amount) }]];
    if (rfs.net != null) rfRows.push([{ v: 'Net takings', bold: true }, { v: fmtN(rfs.net) }]);
    body += '<div style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin:12px 0 8px">Refunds</div>' + buildTable([{ label: 'Refunds made in this period' }, { label: 'Amount', align: 'right' }], rfRows);
  }

  // Metrics summary bar
  const metricsHtml = (metrics || []).map(m => `<div style="text-align:center"><div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:4px">${m.label}</div><div style="font-size:20px;font-weight:700;color:#1f2937">${m.value}</div></div>`).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${reportName}</title>
<style>
  @page { margin: 20mm 15mm; size: A4; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1f2937; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  table { page-break-inside: auto; }
  tr { page-break-inside: avoid; }
</style></head><body>
<div style="border-bottom:3px solid #007EA0;padding-bottom:16px;margin-bottom:24px;display:flex;justify-content:space-between;align-items:flex-end">
  <div>
    <div style="font-size:24px;font-weight:700;color:#1f2937;margin-bottom:4px">${reportName}</div>
    <div style="font-size:13px;color:#6b7280">${dateRange}</div>
  </div>
  <div style="text-align:right">
    <div style="font-size:11px;color:#9ca3af">Generated ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
  </div>
</div>
${metricsHtml ? `<div style="display:flex;justify-content:space-around;padding:16px;background:#f9fafb;border-radius:8px;margin-bottom:24px;border:1px solid #e5e7eb">${metricsHtml}</div>` : ''}
${body}
<div style="margin-top:32px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;display:flex;justify-content:space-between">
  <span>Powered by Beu</span>
  <span>Confidential</span>
</div>
</body></html>`;

  const filename = `${reportName.replace(/\s+/g, '_')}_${dateRange.replace(/\s+/g, '_')}.pdf`;
  const asFile = async () => {
    const blob = await buildPdf({ title: reportName, subtitle: dateRange, metrics: (metrics || []).map(m => ({ label: m.label, value: m.value })), tables: pdfTables });
    return saveFile({ blob, filename, title: reportName });
  };
  // App or touch device: a real PDF through the share sheet / download.
  if (isNativeApp() || isTouchDevice()) return asFile();
  // Desktop: the branded print view (Save as PDF from the dialog). If the
  // popup is blocked (Safari does this), fall back to the file.
  const win = window.open('', '_blank', 'width=800,height=900');
  if (!win) return asFile();
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 300);
  return 'print';
};

// Report Card Component
const ReportCard = ({ report, onSelect }) => {
  const iconMap = {
    chart: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    users: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    refresh: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    box: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    card: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    calculator: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    document: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    cash: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    sun: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    heart: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
    megaphone: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    ),
    gift: (
      <svg width={24} height={24} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
      </svg>
    ),
  };

  return (
    <button
      onClick={() => onSelect(report)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        width: '100%',
        padding: '20px',
        backgroundColor: 'white',
        border: '1px solid #e5e5e5',
        borderRadius: '12px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.15s'
      }}
      onMouseOver={(e) => { e.currentTarget.style.borderColor = '#007EA0'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 126, 160, 0.1)'; }}
      onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e5e5e5'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        backgroundColor: '#f0f9ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#007EA0',
        flexShrink: 0
      }}>
        {iconMap[report.icon] || <DocumentIcon size={24} />}
      </div>
      <div style={{ flex: 1 }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 4px 0' }}>{report.name}</h3>
        <p style={{ fontSize: '13px', color: '#737373', margin: 0 }}>{report.description}</p>
      </div>
      <ChevronRightIcon size={20} />
    </button>
  );
};

// Reports View Component
// App chrome (side nav, mobile menu overlays) goes up to z-index 99999 in
// webindex.css. The report modal sat at 100/101, so the backdrop's blur stopped
// at the content area and the nav and side nav punched through it unblurred.
// Sit the whole modal above the chrome so the blur covers the page.
const MODAL_BACKDROP_Z = 100000;
const MODAL_PANEL_Z = 100001;

const ReportsView = ({ staffFilter = 'all', staffList = [], initialReport = null, salonIds = null, selectedSites = [] }) => {
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  const [selectedReport, setSelectedReport] = useState(null);
  const [generatedReport, setGeneratedReport] = useState(null);
  // Desktop side nav is 250px expanded / 72px collapsed; measure it so the wide
  // report modal centres in the space that's actually visible.
  const isWideReport = !!(generatedReport && generatedReport.reportType === 'staff-earnings');
  const sideNavWidth = (() => {
    try { const el = document.querySelector('.sidenav.menu-show-desktop'); const w = el ? el.getBoundingClientRect().width : 0; return w > 0 && w < 400 ? w : 0; } catch (e) { return 0; }
  })();
  const [isGenerating, setIsGenerating] = useState(false);
  // Multi-location: a group is usually one legal entity, so the VAT report can
  // span every location the caller owns or manages (BE resolves the sites).
  const { multi: multiSite } = useSites();
  // Reports that can roll every site the caller owns/manages into one figure (one legal entity).
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [datePreset, setDatePreset] = useState('mtd');
  const [dateStart, setDateStart] = useState(() => getDateRangeFromPreset('mtd').startDate);
  const [dateEnd, setDateEnd] = useState(() => getDateRangeFromPreset('mtd').endDate);
  const [activeCategory, setActiveCategory] = useState('all');
  const [rebookThreshold, setRebookThreshold] = useState(48);
  const [retentionThreshold, setRetentionThreshold] = useState(90);
  const [staffSortKey, setStaffSortKey] = useState('totalRevenue');
  const [staffSortDir, setStaffSortDir] = useState('desc');

  const handleStaffSort = useCallback((field) => {
    setStaffSortKey(prev => {
      if (prev === field) {
        setStaffSortDir(d => d === 'desc' ? 'asc' : 'desc');
        return field;
      }
      setStaffSortDir('desc');
      return field;
    });
  }, []);

  // API states
  const [reportTypes, setReportTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Derive selected staff name for display
  const selectedStaffName = staffFilter !== 'all'
    ? staffList.find(s => s.id === staffFilter)?.name || 'Staff Member'
    : null;

  // Clear generated report when staff filter changes
  useEffect(() => {
    setGeneratedReport(null);
  }, [staffFilter]);

  // Deep-link: open a specific report when arriving via ?report=<id> (e.g. the
  // "rebook rate" coaching card in Catalogue > Services > Data links here).
  const didAutoOpenReport = useRef(false);
  useEffect(() => {
    if (didAutoOpenReport.current || !initialReport || !reportTypes.length) return;
    const match = reportTypes.find((r) => r.id === initialReport);
    if (match) { didAutoOpenReport.current = true; setSelectedReport(match); }
  }, [initialReport, reportTypes]);

  // Get salonId from localStorage
  const salonId = localStorage.getItem('salonId');

  // Fetch report types on mount
  const fetchReportTypes = useCallback(async () => {
    if (!salonId) {
      setError('No salon selected');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await financeService.getReportTypes({ salonId });
      if (response?.data?.data) {
        setReportTypes(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (err) {
      console.error('Failed to fetch report types:', err);
      // Fall back to default report types from mockData if API fails
      const { reportTypes: defaultTypes } = await import('../data/mockData');
      setReportTypes(defaultTypes || []);
    } finally {
      setLoading(false);
    }
  }, [salonId]);

  useEffect(() => {
    fetchReportTypes();
  }, [fetchReportTypes]);

  const handleGenerateReport = async () => {
    setIsGenerating(true);

    const startDate = dateStart;
    const endDate = dateEnd;

    try {
      const response = await financeService.generateReport({
        salonId,
        reportType: selectedReport.id,
        startDate: formatDateForApi(startDate),
        endDate: formatDateForApi(endDate),
        hairdresserId: staffFilter !== 'all' ? staffFilter : undefined,
        ...(selectedReport.id === 'rebook-rate' && { rebookThresholdHours: rebookThreshold }),
        ...(selectedReport.id === 'client-retention' && { retentionThresholdDays: retentionThreshold }),
        // The Locations filter in the header is the single source of truth now.
        ...(salonIds ? { salonIds } : {}),
        format: 'json'
      });

      const data = response?.data?.data;

      const reportData = data?.data || data;
      setGeneratedReport({
        id: data?.reportId,
        name: selectedReport.name,
        reportType: selectedReport.id,
        dateRange: fmtRangeLabel(startDate, endDate),
        staffName: selectedStaffName,
        rebookThreshold: selectedReport.id === 'rebook-rate' ? rebookThreshold : undefined,
        retentionThreshold: selectedReport.id === 'client-retention' ? retentionThreshold : undefined,
        generatedAt: new Date().toLocaleString('en-GB'),
        metrics: getReportMetrics(selectedReport.id, reportData),
        // Set by the server when the report spans the caller's sites — shown in the header and on exports.
        locationsLabel: Array.isArray(data?.locations) && data.locations.length > 1 ? `All locations: ${data.locations.map((l) => l.name).join(', ')}` : null,
        rawData: reportData
      });
    } catch (err) {
      console.error('Failed to generate report:', err);
      // Show error but still display with default values
      setGeneratedReport({
        name: selectedReport.name,
        reportType: selectedReport.id,
        dateRange: fmtRangeLabel(startDate, endDate),
        generatedAt: new Date().toLocaleString('en-GB'),
        error: 'Failed to generate report data',
        metrics: [{ label: '-', value: '-' }, { label: '-', value: '-' }, { label: '-', value: '-' }]
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const [exporting, setExporting] = useState(null); // 'csv' | 'pdf' | null
  const handleDownload = async (format) => {
    if (!generatedReport?.rawData || exporting) return;
    const opts = { retentionThreshold: generatedReport.retentionThreshold };
    setExporting(format);
    try {
      const rangeLabel = generatedReport.locationsLabel ? `${generatedReport.dateRange} · ${generatedReport.locationsLabel}` : generatedReport.dateRange;
      if (format === 'csv') {
        await exportCSV(generatedReport.reportType, generatedReport.rawData, generatedReport.name, rangeLabel, opts);
      } else {
        await exportPDF(generatedReport.reportType, generatedReport.rawData, generatedReport.name, rangeLabel, generatedReport.metrics, opts);
      }
    } catch (e) {
      console.error('Report export failed:', e);
      window.alert('Could not export the report. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  // Filter reports by category
  const filteredReports = activeCategory === 'all'
    ? reportTypes
    : reportTypes.filter(report => report.category === activeCategory);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1a1a1a', marginBottom: '4px' }}>Reports</h2>
        <p style={{ fontSize: '14px', color: '#737373' }}>Generate and download detailed business reports</p>
      </div>

      {/* Category Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        borderBottom: '1px solid #e5e5e5',
        paddingBottom: '0'
      }}>
        {reportCategories.map((category) => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            style={{
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: activeCategory === category.id ? '600' : '500',
              color: activeCategory === category.id ? '#007EA0' : '#737373',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeCategory === category.id ? '2px solid #007EA0' : '2px solid transparent',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              marginBottom: '-1px',
              transition: 'all 0.15s'
            }}
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* Report Types Grid */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '32px', height: '32px', border: '3px solid #e5e5e5', borderTopColor: '#007EA0', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ fontSize: '14px', color: '#737373' }}>Loading reports...</p>
          </div>
        </div>
      ) : error ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '15px', color: '#ef4444', marginBottom: '12px' }}>{error}</p>
            <button onClick={fetchReportTypes} style={{ padding: '8px 16px', backgroundColor: '#007EA0', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>Try again</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
          {filteredReports.map((report) => (
            <ReportCard key={report.id} report={report} onSelect={setSelectedReport} />
          ))}
        </div>
      )}

      {/* Report Generation Modal */}
      {selectedReport && (
        <>
          {/* Backdrop */}
          <div
            className="sheet-anim-backdrop"
            style={{ position: 'fixed', inset: 0, backgroundColor: isMobile ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: MODAL_BACKDROP_Z }}
            onClick={() => { setSelectedReport(null); setGeneratedReport(null); }}
          />
          {/* Sheet / Modal */}
          <div className={isMobile ? 'sheet-anim-up' : 'sheet-anim-center'} style={isMobile ? {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderRadius: '20px 20px 0 0',
            zIndex: MODAL_PANEL_Z,
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 -4px 40px rgba(0,0,0,0.15)',
            paddingBottom: 'env(safe-area-inset-bottom)'
          } : {
            position: 'fixed',
            top: '50%',
            // Centre in the CONTENT area, not the viewport: at 1360px the modal
            // slid under the side nav and lost its first column (staff names).
            left: isWideReport ? `calc(50% + ${sideNavWidth / 2}px)` : '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            borderRadius: '16px',
            overflow: generatedReport ? 'hidden' : 'visible',
            width: '100%',
            // Staff Earnings is a 15-column ledger — give it the desktop, not a 560px card.
            maxWidth: generatedReport ? (isWideReport ? `min(1360px, calc(100vw - ${sideNavWidth + 40}px))` : '560px') : '480px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            zIndex: MODAL_PANEL_Z,
            boxShadow: '0 24px 48px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.04)'
          }}>
            {isGenerating ? (
              /* Generating State */
              <div style={{ padding: isMobile ? '3rem 2rem' : '4rem 2rem', textAlign: 'center' }}>
                <div style={{
                  width: '48px', height: '48px',
                  border: '3px solid #e5e5e5', borderTopColor: '#1a1a1a',
                  borderRadius: '50%', margin: '0 auto 1.5rem',
                  animation: 'spin 0.8s linear infinite'
                }} />
                <p style={{ fontSize: '16px', fontWeight: '500', color: '#1a1a1a', margin: '0 0 8px 0' }}>Generating Report...</p>
                <p style={{ fontSize: '14px', color: '#737373', margin: 0 }}>{selectedReport.name}</p>
              </div>
            ) : generatedReport ? (
              /* Generated State */
              <>
                {/* Header — dark, matching transactions */}
                <div style={{ backgroundColor: '#1a1a1a', padding: isMobile ? '16px 20px' : '20px 24px', color: 'white', borderRadius: isMobile ? '0' : '16px 16px 0 0', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2px' }}>
                    <svg width={16} height={16} fill="none" stroke="#4ade80" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    <h3 style={{ fontSize: isMobile ? '15px' : '17px', fontWeight: '600', margin: 0 }}>{generatedReport.name}</h3>
                  </div>
                  <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 26px' }}>
                    {generatedReport.dateRange}
                    {generatedReport.locationsLabel && <span style={{ marginLeft: '8px', color: '#4ade80' }}>{generatedReport.locationsLabel}</span>}
                    {generatedReport.staffName && <span style={{ marginLeft: '8px', color: '#4ade80' }}>{generatedReport.staffName}</span>}
                    {generatedReport.rebookThreshold && <span style={{ marginLeft: '8px', color: '#93c5fd' }}>{generatedReport.rebookThreshold < 168 ? `${generatedReport.rebookThreshold}h window` : '7 day window'}</span>}
                    {generatedReport.retentionThreshold && <span style={{ marginLeft: '8px', color: '#93c5fd' }}>{generatedReport.retentionThreshold} day window</span>}
                  </p>
                </div>

                {/* Metrics strip */}
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${(generatedReport.metrics || []).length}, 1fr)`, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                  {(generatedReport.metrics || []).map((m, i) => (
                    <div key={i} style={{ padding: isMobile ? '12px 8px' : '16px 20px', textAlign: 'center', borderRight: i < (generatedReport.metrics || []).length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <p style={{ fontSize: '10px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 3px 0' }}>{m.label}</p>
                      <p style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', color: '#1f2937', margin: 0, fontVariantNumeric: 'tabular-nums' }}>{m.value}</p>
                    </div>
                  ))}
                </div>

                {/* Scrollable report detail — single scroll area */}
                <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: isMobile ? '16px' : '20px 24px' }}>
                  {generatedReport.rawData && (
                    <ReportDetail reportType={generatedReport.reportType} data={generatedReport.rawData} rebookThreshold={generatedReport.rebookThreshold} retentionThreshold={generatedReport.retentionThreshold} staffSortKey={staffSortKey} staffSortDir={staffSortDir} onStaffSort={handleStaffSort} isMobile={isMobile} />
                  )}
                </div>

                {/* Export bar — pinned to bottom */}
                <div style={{ padding: isMobile ? '12px 16px' : '16px 24px', borderTop: '1px solid #e5e7eb', backgroundColor: '#fafafa', flexShrink: 0 }}>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <button
                      onClick={() => handleDownload('pdf')}
                      disabled={!!exporting}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: isMobile ? '14px 16px' : '11px 16px', backgroundColor: '#1a1a1a', color: 'white', border: 'none', borderRadius: '12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      {exporting === 'pdf' ? 'Preparing PDF…' : 'Save as PDF'}
                    </button>
                    <button
                      onClick={() => handleDownload('csv')}
                      disabled={!!exporting}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: isMobile ? '14px 16px' : '11px 16px', backgroundColor: 'white', color: '#1f2937', border: '1px solid #e5e7eb', borderRadius: '12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      {exporting === 'csv' ? 'Preparing CSV…' : 'Export CSV'}
                    </button>
                  </div>
                  <button
                    onClick={() => { setSelectedReport(null); setGeneratedReport(null); }}
                    style={{ width: '100%', padding: '8px', backgroundColor: 'transparent', color: '#9ca3af', border: 'none', fontSize: '13px', cursor: 'pointer' }}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              /* Selection State */
              <>
                <div style={{ padding: isMobile ? '16px 20px' : '24px', borderBottom: '1px solid #e5e5e5', position: 'relative' }}>
                  {isMobile && (
                    <button
                      onClick={() => { setSelectedReport(null); setGeneratedReport(null); }}
                      style={{ position: 'absolute', top: '14px', right: '16px', width: '32px', height: '32px', borderRadius: '50%', border: 'none', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '16px', color: '#1a1a1a' }}
                    >
                      &#x2715;
                    </button>
                  )}
                  <h3 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 4px 0', paddingRight: isMobile ? '40px' : 0 }}>{selectedReport.name}</h3>
                  <p style={{ fontSize: '14px', color: '#737373', margin: 0 }}>{selectedReport.description}</p>
                  {selectedStaffName && (
                    <p style={{ fontSize: '13px', color: '#007EA0', fontWeight: '500', margin: '8px 0 0 0' }}>Staff: {selectedStaffName}</p>
                  )}
                </div>

                <div style={{ padding: isMobile ? '16px 20px 20px' : '24px' }}>
                  {/* Locations now live in the Finance header filter, which covers every view. */}
                  {multiSite && selectedSites.length > 1 && (
                    <p style={{ fontSize: '13px', color: '#737373', margin: '0 0 16px' }}>
                      Covering {selectedSites.map((s) => s.salonName).join(', ')}.
                    </p>
                  )}
                  <p style={{ fontSize: '13px', fontWeight: '500', color: '#737373', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date Range</p>

                  <div style={{ position: 'relative', marginBottom: '24px' }}>
                    <button
                      onClick={() => setDatePickerOpen(!datePickerOpen)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        width: '100%', padding: '12px 16px',
                        backgroundColor: 'white', border: '1px solid #e5e5e5',
                        borderRadius: '10px', fontSize: '14px', cursor: 'pointer'
                      }}
                    >
                      <span style={{ color: '#007EA0', fontWeight: '500' }}>{getDatePresetLabel(datePreset)}</span>
                      <ChevronDownIcon size={16} />
                    </button>

                    <DateRangePicker
                      isOpen={datePickerOpen}
                      onClose={() => setDatePickerOpen(false)}
                      startDate={dateStart}
                      endDate={dateEnd}
                      selectedPreset={datePreset}
                      onPresetChange={setDatePreset}
                      openDirection="up"
                      onApply={(start, end) => {
                        setDateStart(start);
                        setDateEnd(end);
                      }}
                    />
                  </div>

                  {selectedReport.id === 'rebook-rate' && (
                    <div style={{ marginBottom: '24px' }}>
                      <p style={{ fontSize: '13px', fontWeight: '500', color: '#737373', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rebook Window</p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {[24, 48, 72, 168].map(h => (
                          <button
                            key={h}
                            onClick={() => setRebookThreshold(h)}
                            style={{
                              flex: 1, padding: '10px 4px',
                              backgroundColor: rebookThreshold === h ? '#1a1a1a' : 'white',
                              color: rebookThreshold === h ? 'white' : '#737373',
                              border: rebookThreshold === h ? '1px solid #1a1a1a' : '1px solid #e5e5e5',
                              borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {h < 168 ? `${h}h` : '7 days'}
                          </button>
                        ))}
                      </div>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0 0' }}>
                        Client rebooked within {rebookThreshold < 168 ? `${rebookThreshold} hours` : '7 days'} of their appointment
                      </p>
                    </div>
                  )}

                  {selectedReport.id === 'client-retention' && (
                    <div style={{ marginBottom: '24px' }}>
                      <p style={{ fontSize: '13px', fontWeight: '500', color: '#737373', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Retention Window</p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {[30, 60, 90, 120].map(d => (
                          <button
                            key={d}
                            onClick={() => setRetentionThreshold(d)}
                            style={{
                              flex: 1, padding: '10px 4px',
                              backgroundColor: retentionThreshold === d ? '#1a1a1a' : 'white',
                              color: retentionThreshold === d ? 'white' : '#737373',
                              border: retentionThreshold === d ? '1px solid #1a1a1a' : '1px solid #e5e5e5',
                              borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {d} days
                          </button>
                        ))}
                      </div>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0 0' }}>
                        Client booked again within {retentionThreshold} days of their last visit
                      </p>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={() => setSelectedReport(null)}
                      style={{ flex: 1, padding: isMobile ? '14px' : '12px', backgroundColor: 'white', color: '#737373', border: '1px solid #e5e5e5', borderRadius: '12px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleGenerateReport}
                      style={{ flex: 1, padding: isMobile ? '14px' : '12px', backgroundColor: '#1a1a1a', color: 'white', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      Generate Report
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ReportsView;
